import {
  Coordinate,
  getMapData,
  hydrateMapData,
  show3dMap,
  WALLS,
  type Directions,
  type FeatureCollection,
  type Floor,
  type Image3DView,
  type LocationProfile,
  type MapData,
  type MapView,
  type Marker,
  type Path,
  type PointOfInterest,
  type Polygon,
  type Shape,
  type Space,
  type TCameraTarget,
  type TClickPayload,
} from "@mappedin/mappedin-js";
import { config } from "./config";
import { resolveLocalAsset } from "./local-assets";
import type { ExitAnchor, MapTarget } from "./directory";

/** Furthest the camera may zoom in, on every screen size. */
const MAX_ZOOM_LEVEL = 20.5;

let kioskMarker: Marker | null = null;
let destinationMarker: Marker | null = null;
let liveLocationWatchId: number | null = null;

// POI marker tracking
const poiMarkers: Marker[] = [];

// Location logo marker tracking
const locationLogoMarkers: Marker[] = [];
/** The stand-click handler registered by `onSpaceClick` — also fired by taps on a stand's logo. */
let standClickHandler: ((space: Space) => void) | null = null;

/**
 * Makes a marker's logo act like a tap on its stand. Logos are DOM overlays,
 * so a tap on one never reaches the map's own raycast (and the logo isn't
 * always over its stand's floor), so it's routed to the stand directly.
 */
function bindLogoClick(marker: Marker, selector: string, space: Space): void {
  marker.contentEl.querySelector(selector)?.addEventListener("click", (event) => {
    event.stopPropagation();
    standClickHandler?.(space);
  });
}

export function updateCameraSafeArea(mapView: MapView): void {
  const width = window.innerWidth;
  if (width < 640) {
    mapView.Camera.setScreenOffsets({ left: 12, top: 64, right: 12, bottom: 240, type: "pixel" });
  } else if (width < 1024) {
    mapView.Camera.setScreenOffsets({ left: 340, top: 20, right: 70, bottom: 20, type: "pixel" });
  } else {
    mapView.Camera.setScreenOffsets({ left: 420, top: 24, right: 80, bottom: 24, type: "pixel" });
  }
}

/**
 * Hydrates from a local MVF bundle (fetched ahead of time via `npm run
 * fetch-mvf`) instead of calling Mappedin's cloud API. This is what lets
 * the kiosk render — including on a refresh — with no internet
 * connection, which matters in a crowded venue with flaky WiFi.
 */
async function hydrateFromLocalBundle(): Promise<MapData> {
  const response = await fetch(config.mvfBundleUrl);
  if (!response.ok) {
    throw new Error(`Offline map bundle not found (${response.status}): ${config.mvfBundleUrl}`);
  }
  const bundle = new Uint8Array(await response.arrayBuffer());
  return hydrateMapData(
    // Telling hydrateMapData the bundle's format upfront skips its own
    // v2-then-v3 detection attempt (which logs a console error on a miss).
    // `scripts/fetch-full-mvf.mjs` downloads v2 — see `config.mvfBundleVersion`.
    { type: "binary", main: bundle, options: { version: config.mvfBundleVersion } },
    { key: config.key, secret: config.secret, mapId: config.mapId },
  );
}

export async function initMap(container: HTMLElement): Promise<{ mapData: MapData; mapView: MapView }> {
  const mapData =
    config.mapMode === "online"
      ? // Always calls Mappedin's cloud API — requires internet on every
      // load, including a refresh. See VITE_MAP_MODE in .env.example.
      await getMapData({ key: config.key, secret: config.secret, mapId: config.mapId })
      : await hydrateFromLocalBundle();

  const mapView = await show3dMap(container, mapData, {
    pitch: 55,
    bearing: 0,
    // Drops the "Provide Feedback" link next to the map attribution. The
    // attribution text itself ("Mappedin", "StreetMap"/OpenStreetMap) is
    // left in place — OSM's license requires that credit stay visible, so
    // it isn't something to suppress via config or CSS.
    attribution: { feedback: false },
  });

  // Hide the Mappedin branding watermark and disable its hitbox — the SDK's
  // watermark is an interactive element whose default onClick opens
  // mappedin.com, so `visible: false` alone would still swallow taps in
  // the corner where it sits.
  mapView.updateWatermark({ visible: false, interactive: false });

  // Stand walls (the interior dividers between booths), shorter than their
  // real height so the top-down view into neighboring stands isn't fully
  // blocked. Tune via VITE_STAND_WALL_HEIGHT — see config.ts; this is NOT
  // forced to full height by VITE_STAND_AS_BOX (see that config's own
  // comment for why). VITE_STAND_WALL_COLOR is a single shared color for
  // every interior wall in the venue — WALLS.Interior has no way to target
  // one stand's walls individually, unlike the per-stand floor tint in
  // `applyStandColorRanges`.
  mapView.updateState(WALLS.Interior, {
    height: config.standWallHeight,
    ...(config.standWallColor ? { color: config.standWallColor } : {}),
  });

  // Enable all camera interactions (pan, zoom, bearing & pitch rotation via touch/drag)
  mapView.Camera.interactions.enable();
  mapView.Camera.setMinPitch(0);
  mapView.Camera.setMaxPitch(75);

  // Cap how far in pinch/scroll/+ can zoom, on every screen size — past
  // this the map no longer renders usefully.
  mapView.Camera.setMaxZoomLevel(MAX_ZOOM_LEVEL);

  updateCameraSafeArea(mapView);
  window.addEventListener("resize", () => {
    try {
      updateCameraSafeArea(mapView);
    } catch { }
  });

  return { mapData, mapView };
}

/**
 * Frames the camera so the entire venue floor is in view.
 */
export function focusWholeVenue(mapView: MapView, mapData: MapData): void {
  const spaces = mapData.getByType("space");
  if (spaces.length === 0) return;
  void mapView.Camera.focusOn(spaces, { duration: 800 });
}

/**
 * Smoothly rotates the camera by deltaDegrees (e.g. +45 or -45 deg).
 */
export function rotateCamera(mapView: MapView, deltaDegrees: number): void {
  const currentBearing = mapView.Camera.bearing;
  const newBearing = (currentBearing + deltaDegrees + 360) % 360;
  void mapView.Camera.animateTo(
    { bearing: newBearing },
    { duration: 450, easing: "ease-out" },
  );
}

/**
 * Resets camera bearing to North (0 degrees).
 */
export function resetNorth(mapView: MapView): void {
  void mapView.Camera.animateTo(
    { bearing: 0 },
    { duration: 500, easing: "ease-out" },
  );
}

/**
 * Switches between top-down 2D view and tilted 3D perspective side view.
 * `preserveBearing` (used when rotation is locked) skips the dramatic 25°
 * auto-turn on entering 3D, so the fixed orientation the operator locked in
 * isn't disturbed by tilting.
 */
export function setPitchMode(mapView: MapView, mode: "2d" | "3d", preserveBearing = false): void {
  const targetPitch = mode === "3d" ? 55 : 0;
  const currentBearing = mapView.Camera.bearing;
  // If switching to 3D with 0 bearing, give it a subtle 25° angle for dramatic 3D perspective
  const targetBearing = mode === "3d" && currentBearing === 0 && !preserveBearing ? 25 : currentBearing;
  void mapView.Camera.animateTo(
    { pitch: targetPitch, bearing: targetBearing },
    { duration: 700, easing: "ease-in-out" },
  );
}

/**
 * Zooms camera in or out smoothly.
 */
export function zoomCamera(mapView: MapView, delta: number): void {
  const currentZoom = mapView.Camera.zoomLevel;
  void mapView.Camera.animateTo(
    { zoomLevel: Math.min(MAX_ZOOM_LEVEL, Math.max(12, currentZoom + delta)) },
    { duration: 350, easing: "ease-out" },
  );
}

/**
 * Adds exhibitor text labels directly on the 3D map. Locations that have
 * their own logo (handled by `renderLocationLogos`) are skipped here — the
 * logo marker carries the name itself, since Mappedin's collision solver
 * always resolves an overlapping Label vs. Marker in favor of one or the
 * other rather than showing both, however they're ranked or placed.
 */
export function addExhibitorLabels(mapView: MapView, directory: { name: string; space: Space; iconUrl?: string }[]): void {
  for (const entry of directory) {
    if (entry.iconUrl) continue;
    try {
      mapView.Labels.add(entry.space, entry.name, {
        rank: "medium",
        interactive: false,
      });
    } catch {
      // Ignored if label cannot anchor
    }
  }
}

/**
 * Places each location's own uploaded logo (`LocationProfile.icon`), together
 * with its name, at the center of its space — for the subset of locations
 * that have a logo set in Mappedin admin. The name is baked into the same
 * marker (rather than left to the separate exhibitor Label) so the two
 * can't collide and suppress one another. Non-interactive — taps pass
 * through to the space underneath so click-to-open still works.
 *
 * The name itself is omitted entirely when VITE_HIDE_LOCATION_NAME=true
 * (see `config.hideLocationName`), leaving just the bare logo — or, for a
 * categorized location, when VITE_HIDE_CATEGORY_LOCATION_NAME=true (which
 * follows VITE_HIDE_LOCATION_NAME when unset). The logo's
 * crop shape (circle/square/auto) follows `config.logoShape` — see the
 * `location-logo-marker.shape-*` rules in style.css.
 *
 * A location that has a category assigned in Mappedin admin (amenities
 * like restrooms/F&B/VR area, per this venue's data — real exhibitor
 * stands are never categorized) renders its logo at half size (the
 * `.has-category` class in style.css) even if a real logo was uploaded for
 * it, so it never reads as prominently as an actual exhibitor's logo.
 *
 * The whole feature can be switched off with VITE_SHOW_LOCATION_LOGOS=false
 * (see `config.showLocationLogos`) — no markers are created and no logo
 * images are ever requested from the browser.
 */
export function renderLocationLogos(
  mapView: MapView,
  entries: { space: Space; name: string; iconUrl?: string; categoryNames: string[] }[],
): void {
  clearLocationLogos(mapView);
  if (!config.showLocationLogos) return;
  for (const entry of entries) {
    if (!entry.iconUrl) continue;
    try {
      const isCategory = entry.categoryNames.length > 0;
      const hideName = isCategory ? config.hideCategoryLocationName : config.hideLocationName;
      const nameHtml = hideName ? "" : `<span class="location-logo-name">${entry.name}</span>`;
      const sizeClass = isCategory ? " has-category" : "";
      const marker = mapView.Markers.add(
        entry.space,
        `<div class="location-logo-marker shape-${config.logoShape}${sizeClass}" aria-label="${entry.name} logo">
          <img src="${entry.iconUrl}" alt="" />
          ${nameHtml}
        </div>`,
        // Not "pointer-events-auto": that makes the SDK's whole marker box
        // catch taps — an invisible box over the stand that swallowed stand
        // clicks. Only the logo <img> takes pointer events (style.css).
        { rank: "high", interactive: false },
      );
      if (marker) {
        bindLogoClick(marker, ".location-logo-marker img", entry.space);
        locationLogoMarkers.push(marker);
      }
    } catch {
      // Space may not be anchorable on this floor
    }
  }
}

export function clearLocationLogos(mapView: MapView): void {
  for (const m of locationLogoMarkers) {
    try { mapView.Markers.remove(m); } catch { }
  }
  locationLogoMarkers.length = 0;
}

/**
 * Persistently tints each real numbered stand's floor (see `standNumericId`
 * — a named area like "Hall 1" is never touched here) with its own
 * `standColorRanges` color, exactly following its real polygon shape (so
 * it never overflows past a non-rectangular stand's actual walls the way a
 * bounding-rectangle `Image3D` layer would). A stand with no matching
 * range is left untouched here — when `standAsBox` is on, its box still
 * gets covered with the flat `standBoxCoverColor` fallback, just as a
 * raised rectangular cap instead (see `renderLocationPhotos`), since that
 * needs to sit on top rather than just tint the floor.
 *
 * Skips any stand that has its own local `public/stands/<externalId>.*`
 * photo (the same one `renderLocationPhotos` shows): that photo already
 * covers/raises above the floor, so tinting underneath it just left a
 * mismatched strip of color peeking out around the photo's edges instead
 * of a clean, uncolored floor. The colored wall shape (`standColoredWalls`,
 * see `applyStandColoredWalls`) follows the same skip — reserved for the
 * fallback (no-photo) case too. Applied once at boot and never reset,
 * unlike `highlightZone`'s transient search/filter highlight.
 */
export async function applyStandColorRanges(
  mapView: MapView,
  entries: { space: Space; location: LocationProfile }[],
): Promise<void> {
  if (!config.showStandColorRanges && !config.categoryStandColor) return;

  let matched = 0;
  const coloredEntries: { space: Space; color: string }[] = [];
  // Several locations can share one space (e.g. two "VR AREA" records on the
  // same polygon). Color it once — a second identical ring would "touch" the
  // first along every edge, and `applyStandColoredWalls` would notch the
  // whole wall away.
  // Entries whose External ID is listed by name come first, so on a shared
  // space their explicit color is the one kept.
  const ordered = [...entries].sort(
    (a, b) =>
      Number(Boolean(namedIdColor(standExternalId(b.space, b.location)))) -
      Number(Boolean(namedIdColor(standExternalId(a.space, a.location)))),
  );
  const coloredSpaceIds = new Set<string>();
  for (const entry of ordered) {
    if (coloredSpaceIds.has(entry.space.id)) continue;
    const externalId = standExternalId(entry.space, entry.location);
    // An External ID listed by name (e.g. "VR AREA") is the most specific
    // choice and wins outright. Otherwise a category match (a restroom/
    // cafeteria — see `config.categoryStandColor`'s own comment) wins over a
    // numeric range match, and works even with no External ID at all (most
    // amenities have none), unlike `standRangeColor`.
    const color = namedIdColor(externalId) || categoryColor(entry.location) || standRangeColor(externalId);
    if (!color) continue;
    matched++;
    coloredSpaceIds.add(entry.space.id);

    const hasPhoto = config.showLocationPhotos && externalId && (await localStandImageUrl(externalId));
    if (hasPhoto) continue;

    // Reserved for the fallback (no-photo) case, same as the floor tint —
    // a stand with its own photo doesn't get the colored wall either. The
    // actual Shape isn't built yet — see `applyStandColoredWalls`, which
    // needs every colored stand's boundary known up front to cut gaps
    // where two of them touch.
    if (config.showStandColoredWalls) coloredEntries.push({ space: entry.space, color });
    try {
      mapView.updateState(entry.space, { color, opacity: 1 });
    } catch {
      // Space may not be updatable on this floor
    }
  }
  const coloredWalls = config.showStandColoredWalls ? applyStandColoredWalls(mapView, coloredEntries) : 0;
  console.log(
    `[applyStandColorRanges] ${matched} stand(s) matched a color range` +
    (config.showStandColoredWalls ? `; ${coloredWalls} colored wall shape(s) added` : ""),
  );
}

const standShapes: Shape[] = [];
/** Each stand's own coloured-wall box — raised with the destination arrival effect. */
const standWallShapeBySpaceId = new Map<string, Shape>();

/** A straight [start, end] segment in local meters — one edge of a stand's own ring. */
interface EdgeSegment {
  a: LocalPoint;
  b: LocalPoint;
}

/** The overlapping stretch between two close, parallel edges — see `segmentsOverlapClose`. */
interface EdgeOverlap {
  /**
   * The margin-clamped stretch (kept clear of both edges' own corners) —
   * for `notchRing`'s cut, which modifies the ring itself and would
   * self-intersect at a corner without that clearance (see this
   * function's own comment for why).
   */
  notch: { onA: EdgeSegment; onB: EdgeSegment };
  /**
   * The FULL stretch, unclamped — for `addGapFillerQuad`'s fill, which is
   * an independent shape with no ring topology of its own to protect, so
   * it can safely run all the way to (and past, when two shared walls
   * meet at a right angle) each edge's true corner. Where a stand's
   * shared boundary bends — two separate edge-pairs meeting at one
   * corner — this is what lets their two fill quads actually reach and
   * overlap at that corner instead of both stopping short of it, which
   * would otherwise leave a visible wedge of unfilled color right at the
   * bend.
   */
  fill: { onA: EdgeSegment; onB: EdgeSegment };
}

/**
 * If `s1` and `s2` are roughly parallel (within ~14°) and close together
 * (within `maxGap`), returns the overlapping stretch of each segment's own
 * line — `null` if they're not parallel, too far apart, or the overlap
 * (after the `cornerMargin` clearance used for the notch) is shorter than
 * `minOverlap` (a near-miss corner touch, not a real shared wall). Used to
 * find exactly which portion of two stands' edges actually face each
 * other, rather than assuming their whole perimeters do. See `EdgeOverlap`
 * for why this returns two different-length versions of the same stretch.
 * Verified against synthetic corner-to-corner, partial-overlap, and
 * right-angle-bend (an L-shaped shared boundary) rectangles before
 * shipping.
 */
function segmentsOverlapClose(
  s1: EdgeSegment,
  s2: EdgeSegment,
  maxGap: number,
  minOverlap: number,
  cornerMargin: number,
): EdgeOverlap | null {
  const len1 = Math.hypot(s1.b[0] - s1.a[0], s1.b[1] - s1.a[1]);
  const len2 = Math.hypot(s2.b[0] - s2.a[0], s2.b[1] - s2.a[1]);
  if (len1 < 1e-6 || len2 < 1e-6) return null;

  const dir1: LocalPoint = [(s1.b[0] - s1.a[0]) / len1, (s1.b[1] - s1.a[1]) / len1];
  const dir2: LocalPoint = [(s2.b[0] - s2.a[0]) / len2, (s2.b[1] - s2.a[1]) / len2];
  const dot = Math.abs(dir1[0] * dir2[0] + dir1[1] * dir2[1]);
  if (dot < 0.97) return null;

  // Perpendicular distance from s2's midpoint to the infinite line through s1.
  const normal: LocalPoint = [-dir1[1], dir1[0]];
  const midX = (s2.a[0] + s2.b[0]) / 2;
  const midY = (s2.a[1] + s2.b[1]) / 2;
  const perpDist = Math.abs((midX - s1.a[0]) * normal[0] + (midY - s1.a[1]) * normal[1]);
  if (perpDist > maxGap) return null;

  const project1 = (p: LocalPoint) => (p[0] - s1.a[0]) * dir1[0] + (p[1] - s1.a[1]) * dir1[1];
  const rawLo = Math.max(0, Math.min(project1(s2.a), project1(s2.b)));
  const rawHi = Math.min(len1, Math.max(project1(s2.a), project1(s2.b)));
  const lo = Math.max(rawLo, cornerMargin);
  const hi = Math.min(rawHi, len1 - cornerMargin);
  if (hi - lo < minOverlap) return null;

  const notchOnA: EdgeSegment = {
    a: [s1.a[0] + dir1[0] * lo, s1.a[1] + dir1[1] * lo],
    b: [s1.a[0] + dir1[0] * hi, s1.a[1] + dir1[1] * hi],
  };
  const fillOnA: EdgeSegment = {
    a: [s1.a[0] + dir1[0] * rawLo, s1.a[1] + dir1[1] * rawLo],
    b: [s1.a[0] + dir1[0] * rawHi, s1.a[1] + dir1[1] * rawHi],
  };

  // Both onA versions, re-projected onto s2's own line — s1 and s2 are
  // close and near-parallel but essentially never exactly collinear, so
  // reusing onA's points directly as s2's stretch would land slightly off
  // s2's edge, opening a stray spike/self-intersection in s2's ring
  // instead of a clean notch.
  const project2 = (p: LocalPoint) => (p[0] - s2.a[0]) * dir2[0] + (p[1] - s2.a[1]) * dir2[1];

  const nt1 = Math.max(0, Math.min(len2, project2(notchOnA.a)));
  const nt2 = Math.max(0, Math.min(len2, project2(notchOnA.b)));
  const notchTMin = Math.max(Math.min(nt1, nt2), cornerMargin);
  const notchTMax = Math.min(Math.max(nt1, nt2), len2 - cornerMargin);
  if (notchTMax - notchTMin < minOverlap * 0.5) return null;
  const notchOnB: EdgeSegment = {
    a: [s2.a[0] + dir2[0] * notchTMin, s2.a[1] + dir2[1] * notchTMin],
    b: [s2.a[0] + dir2[0] * notchTMax, s2.a[1] + dir2[1] * notchTMax],
  };

  const ft1 = Math.max(0, Math.min(len2, project2(fillOnA.a)));
  const ft2 = Math.max(0, Math.min(len2, project2(fillOnA.b)));
  const fillOnB: EdgeSegment = {
    a: [s2.a[0] + dir2[0] * Math.min(ft1, ft2), s2.a[1] + dir2[1] * Math.min(ft1, ft2)],
    b: [s2.a[0] + dir2[0] * Math.max(ft1, ft2), s2.a[1] + dir2[1] * Math.max(ft1, ft2)],
  };

  return { notch: { onA: notchOnA, onB: notchOnB }, fill: { onA: fillOnA, onB: fillOnB } };
}

/**
 * Fills the exact quadrilateral between two stands' ORIGINAL (pre-notch)
 * outset boundaries — `onA` and `onB` from `segmentsOverlapClose`, the
 * same stretch `notchRing` just carved both of their walls back from —
 * with a plain `standBorderLineColor` fill, extruded floor-to-
 * `standColoredWallHeight`. Using the pre-notch boundaries (rather than a
 * fixed-width strip guessed at the midline) guarantees full coverage no
 * matter how wide the real gap between the two stands actually is: two
 * stands sharing one real wall typically overlap here (by design — see
 * `standColoredWallOutset`'s own comment), so a strip sized to just
 * `standBorderLineWidth` could still leave a sliver of the venue's real
 * (likely gray) floor/wall exposed at each edge.
 *
 * `onA` and `onB` trace their shared stretch in OPPOSITE directions (each
 * ring winds around its own polygon the opposite way past a shared edge),
 * so `onA.a` pairs with whichever of `onB.a`/`onB.b` is actually nearest
 * to it — determined by distance rather than assumed, since ring winding
 * isn't guaranteed consistent across every stand in this venue's data.
 * Verified against a synthetic shared-wall pair (both directly touching
 * and with an artificial gap) before shipping: the resulting quad stays a
 * simple, non-self-intersecting polygon and its footprint fully contains
 * both stands' notched boundaries in each case.
 */
function addGapFillerQuad(mapView: MapView, onA: EdgeSegment, onB: EdgeSegment, lon0: number, lat0: number, floor: Floor): void {
  if (!config.standBorderLineColor) return;

  const distAA = Math.hypot(onA.a[0] - onB.a[0], onA.a[1] - onB.a[1]);
  const distAB = Math.hypot(onA.a[0] - onB.b[0], onA.a[1] - onB.b[1]);
  const [near, far] = distAA <= distAB ? [onB.a, onB.b] : [onB.b, onB.a];
  addBorderFillShape(mapView, [onA.a, onA.b, far, near, onA.a], lon0, lat0, floor, "addGapFillerQuad");
}

/**
 * Plugs a small square, `halfSize` meters to each side of `center`, with a
 * plain `standBorderLineColor` fill — used at a ring vertex where two
 * separate shared-wall runs meet (an L-bend in a stand's own boundary, or
 * three-plus stands meeting at one point). Each run's own fill quad
 * (`addGapFillerQuad`) is independently kept clear of that vertex by the
 * SAME corner margin its notch uses (both derived from the stretch
 * `segmentsOverlapClose` returns), which can leave a small square of the
 * stand's own color exposed right at the vertex where neither quad quite
 * reaches — this patches exactly that square directly, rather than trying
 * to extend either fill further (a bend can involve two different
 * neighbors' quads, not one continuous pair, so there's no single quad to
 * safely extend without also affecting its own straight run).
 */
function addCornerPatch(mapView: MapView, center: LocalPoint, halfSize: number, lon0: number, lat0: number, floor: Floor): void {
  if (!config.standBorderLineColor) return;
  const quadLocal: LocalPoint[] = [
    [center[0] - halfSize, center[1] - halfSize],
    [center[0] + halfSize, center[1] - halfSize],
    [center[0] + halfSize, center[1] + halfSize],
    [center[0] - halfSize, center[1] + halfSize],
    [center[0] - halfSize, center[1] - halfSize],
  ];
  addBorderFillShape(mapView, quadLocal, lon0, lat0, floor, "addCornerPatch");
}

/** Shared `Shapes.add` call behind `addGapFillerQuad` and `addCornerPatch` — see either's own comment for what each is filling. */
function addBorderFillShape(mapView: MapView, localRing: LocalPoint[], lon0: number, lat0: number, floor: Floor, callerName: string): void {
  const ring = localRing.map((p) => localMetersToLonLat(p, lon0, lat0));
  const featureCollection: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } as unknown as Polygon }],
  };
  try {
    const shape = mapView.Shapes.add(
      featureCollection,
      {
        color: config.standBorderLineColor,
        topColor: config.standBorderLineColor,
        // Slightly taller than the stands' own colored walls on purpose:
        // this fill deliberately overlaps a sliver of each stand's
        // remaining (non-notched) top face, and two top faces sitting at
        // the exact same height there would z-fight (flicker) instead of
        // cleanly rendering the fill on top. 2cm clears that without
        // being visually noticeable at kiosk viewing distance.
        height: config.standColoredWallHeight + 0.02,
        altitude: 0,
        opacity: 1,
        outline: false,
      },
      floor,
    );
    if (shape) standShapes.push(shape);
  } catch (err) {
    console.warn(`[${callerName}] Failed to add fill shape:`, err);
  }
}

/** One stretch of `ring`'s own edge `edgeIndex` (between its vertices `edgeIndex` and `edgeIndex + 1`) to pull inward. */
interface RingCut {
  edgeIndex: number;
  a: LocalPoint;
  b: LocalPoint;
}

/**
 * Cuts a rectangular notch `inset` meters into `ring` at each entry in
 * `cuts` — opens a real physical gap in the polygon's own boundary at
 * exactly the stretch named by each cut, leaving the rest of the ring
 * untouched. Used so two adjacent stands' colored walls visibly separate
 * with a break in the geometry itself, rather than one box's wall meeting
 * (or a painted line sitting on top of) its neighbor's.
 */
function notchRing(ring: LocalPoint[], cuts: RingCut[], inset: number): LocalPoint[] {
  const closed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
  const pts = closed ? ring.slice(0, -1) : ring;
  const m = pts.length;
  if (m < 3) return ring;

  let signedArea = 0;
  for (let i = 0; i < m; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % m];
    signedArea += x1 * y2 - x2 * y1;
  }
  const ccw = signedArea > 0;

  const byEdge = new Map<number, RingCut[]>();
  for (const cut of cuts) {
    const list = byEdge.get(cut.edgeIndex);
    if (list) list.push(cut);
    else byEdge.set(cut.edgeIndex, [cut]);
  }

  const result: LocalPoint[] = [];
  for (let i = 0; i < m; i++) {
    const curr = pts[i];
    const next = pts[(i + 1) % m];
    result.push(curr);

    const edgeCuts = byEdge.get(i);
    if (!edgeCuts) continue;

    // Same outward-normal convention as `offsetRingOutward`; subtracting it
    // (rather than adding) is what pulls these two points inward.
    const dir = normalizeVector([next[0] - curr[0], next[1] - curr[1]]);
    const normal: LocalPoint = ccw ? [dir[1], -dir[0]] : [-dir[1], dir[0]];
    const project = (p: LocalPoint) => (p[0] - curr[0]) * dir[0] + (p[1] - curr[1]) * dir[1];

    const sorted = [...edgeCuts].sort(
      (c1, c2) => Math.min(project(c1.a), project(c1.b)) - Math.min(project(c2.a), project(c2.b)),
    );
    for (const cut of sorted) {
      const [a, b] = project(cut.a) <= project(cut.b) ? [cut.a, cut.b] : [cut.b, cut.a];
      const aIn: LocalPoint = [a[0] - normal[0] * inset, a[1] - normal[1] * inset];
      const bIn: LocalPoint = [b[0] - normal[0] * inset, b[1] - normal[1] * inset];
      result.push(a, aIn, bIn, b);
    }
  }
  result.push(result[0]);
  return result;
}

/**
 * Creates one extruded, colored 3D `Shape` per entry, traced from each
 * stand's own real floor polygon (true vertical walls AND a top face, both
 * in that stand's own color), pushed outward by `standColoredWallOutset`
 * — see that config's own comment for why: sized to the exact boundary, a
 * shape renders hidden behind the real, thicker `WALLS.Interior` geometry
 * everywhere except at a door gap.
 *
 * Where two stands' outward-pushed boundaries would otherwise touch or
 * overlap, a real notch (`notchRing`) is cut into both of their rings at
 * that stretch instead — a physical break in the geometry so they visibly
 * separate, rather than blending into one continuous colored mass or
 * relying on a painted line sitting on top to tell them apart. Every
 * stand's ring is projected into ONE shared local-meters frame first
 * (`ringToLocalMeters`'s own per-ring origin default would make
 * cross-stand distance comparisons meaningless), then every edge of every
 * stand is checked against every edge of every other stand for a close,
 * parallel, overlapping stretch — see `segmentsOverlapClose`.
 * O(stands² �— edges²), which is fine for a one-time boot computation at
 * this venue's scale (~100 colored stands). Returns how many shapes were
 * added successfully.
 */
function applyStandColoredWalls(mapView: MapView, entries: { space: Space; color: string }[]): number {
  if (entries.length === 0) return 0;

  let origin: [number, number] | null = null;
  for (const entry of entries) {
    const geometry = entry.space.geoJSON.geometry as { type: string; coordinates?: unknown };
    if (geometry.type !== "Polygon") continue;
    const ring = (geometry.coordinates as [number, number][][] | undefined)?.[0];
    if (ring && ring.length > 0) {
      origin = ring[0];
      break;
    }
  }
  if (!origin) return 0;
  const [lon0, lat0] = origin;

  const outsetRings: (LocalPoint[] | null)[] = entries.map((entry) => {
    const geometry = entry.space.geoJSON.geometry as { type: string; coordinates?: unknown };
    if (geometry.type !== "Polygon") return null;
    const ring = (geometry.coordinates as [number, number][][] | undefined)?.[0];
    if (!ring || ring.length < 3) return null;
    return offsetRingOutward(ringToLocalMeters(ring, lon0, lat0), config.standColoredWallOutset);
  });

  // Gated on `standBorderLineColor` purely as an on/off toggle now — there's
  // nothing left to paint at a gap, but this keeps the existing env var
  // meaning "separate touching stands" without adding a new one.
  const gapEnabled = Boolean(config.standBorderLineColor) && entries.length > 1;
  const inset = config.standBorderLineWidth / 2;
  const cutsByEntry: RingCut[][] = entries.map(() => []);
  if (gapEnabled) {
    const MAX_GAP = 1; // meters — generous enough for a typical thin partition wall between stands
    const MIN_OVERLAP = 0.5; // meters — ignore a near-miss corner touch
    // Kept clear of each edge's own corners — see `segmentsOverlapClose`'s
    // own comment for why. Twice the inset is enough margin for the
    // rectangular (90°-and-up) corners real stands actually have.
    const CORNER_MARGIN = inset * 2;
    for (let i = 0; i < entries.length; i++) {
      const ringA = outsetRings[i];
      if (!ringA || ringA.length < 3) continue;
      for (let j = i + 1; j < entries.length; j++) {
        const ringB = outsetRings[j];
        if (!ringB || ringB.length < 3) continue;

        for (let ei = 0; ei < ringA.length - 1; ei++) {
          const segA: EdgeSegment = { a: ringA[ei], b: ringA[ei + 1] };
          for (let ej = 0; ej < ringB.length - 1; ej++) {
            const segB: EdgeSegment = { a: ringB[ej], b: ringB[ej + 1] };
            const overlap = segmentsOverlapClose(segA, segB, MAX_GAP, MIN_OVERLAP, CORNER_MARGIN);
            if (!overlap) continue;
            cutsByEntry[i].push({ edgeIndex: ei, a: overlap.notch.onA.a, b: overlap.notch.onA.b });
            cutsByEntry[j].push({ edgeIndex: ej, a: overlap.notch.onB.a, b: overlap.notch.onB.b });

            addGapFillerQuad(mapView, overlap.fill.onA, overlap.fill.onB, lon0, lat0, entries[i].space.floor);
          }
        }
      }
    }

    // A stand whose own ring bends where its shared boundary does (an
    // L-shaped stand, or three-plus stands meeting at one point) gets a
    // notch cut on BOTH edges either side of that vertex — see
    // `addCornerPatch`'s own comment for why that can leave a small
    // square of its own color exposed right at the vertex.
    for (let i = 0; i < entries.length; i++) {
      const ring = outsetRings[i];
      const cuts = cutsByEntry[i];
      if (!ring || cuts.length < 2) continue;
      const m = ring.length - 1;
      const edgesWithCuts = new Set(cuts.map((c) => c.edgeIndex));
      for (const edgeIndex of edgesWithCuts) {
        const nextEdge = (edgeIndex + 1) % m;
        if (edgesWithCuts.has(nextEdge)) {
          addCornerPatch(mapView, ring[nextEdge], CORNER_MARGIN * 1.5, lon0, lat0, entries[i].space.floor);
        }
      }
    }
  }

  let added = 0;
  for (let i = 0; i < entries.length; i++) {
    const outsetRing = outsetRings[i];
    if (!outsetRing) {
      console.warn("[applyStandColoredWalls] Space has no usable outer ring:", entries[i].space.id);
      continue;
    }
    const finalRing = cutsByEntry[i].length > 0 ? notchRing(outsetRing, cutsByEntry[i], inset) : outsetRing;
    const lonLatRing = finalRing.map((p) => localMetersToLonLat(p, lon0, lat0));

    const featureCollection: FeatureCollection<Polygon> = {
      type: "FeatureCollection",
      features: [
        { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [lonLatRing] } as unknown as Polygon },
      ],
    };

    try {
      const shape = mapView.Shapes.add(
        featureCollection,
        {
          color: entries[i].color,
          topColor: entries[i].color,
          height: config.standColoredWallHeight,
          altitude: 0,
          opacity: 1,
          // Without this, the shape gets its own default outline stroke
          // traced around the top face — a stray gray border on an
          // otherwise fully-colored box.
          outline: false,
        },
        entries[i].space.floor,
      );
      if (shape) {
        // This box is decoration drawn ON TOP of the stand's real floor
        // polygon, and a newly added Shape is `interactive: false` — which
        // in this SDK still CAPTURES the raycast and shields whatever is
        // behind it, it just doesn't fire a handler of its own. So every
        // stand with a range color swallowed its own tap: the click stopped
        // on the box, `event.spaces` came back empty, `onSpaceClick` found
        // nothing to route, and the tap degraded into a plain camera drag
        // with no place card and no zoom. 'pass-through' skips the box
        // entirely so the click lands on the stand's own floor instead.
        mapView.updateState(shape, { interactive: "pass-through" });
        standShapes.push(shape);
        standWallShapeBySpaceId.set(entries[i].space.id, shape);
        added++;
      } else {
        console.warn("[applyStandColoredWalls] Shapes.add returned falsy for space:", entries[i].space.id);
      }
    } catch (err) {
      console.warn("[applyStandColoredWalls] Failed to add Shape for space:", entries[i].space.id, err);
    }
  }
  return added;
}

export function clearStandColoredWalls(mapView: MapView): void {
  for (const shape of standShapes) {
    try { mapView.Shapes.remove(shape); } catch { }
  }
  standShapes.length = 0;
  standWallShapeBySpaceId.clear();
}

/** Color for an External ID listed by name (not a number/range) in VITE_STAND_COLOR_RANGES, if any. */
function namedIdColor(externalId: string | undefined): string | undefined {
  if (!config.showStandColorRanges || !externalId) return undefined;
  return config.standColorRanges.ids[externalId];
}

/** Looks up the configured `standColorRanges` color for a stand's own External ID, if any — shared by `applyStandColorRanges` (the floor) and `applyBoxCover` (the box's top cap). */
function standRangeColor(externalId: string | undefined): string | undefined {
  if (!config.showStandColorRanges || !externalId) return undefined;

  // A literal External ID match (e.g. a named "F&B AREA HALL 2" target)
  // works regardless of whether the ID is numeric — checked first since
  // it's the more specific kind of match.
  const idMatch = config.standColorRanges.ids[externalId];
  if (idMatch) return idMatch;

  const num = standNumericId(externalId);
  if (num === undefined) return undefined;
  return config.standColorRanges.ranges.find((r) => num >= r.min && num <= r.max)?.color;
}

/**
 * Color for a categorized location (a restroom/cafeteria/prayer room/VR
 * area) — its specific category's `config.categoryColors` override if one
 * is set (e.g. "Special Area" → white), otherwise the general
 * `categoryStandColor` fallback. `undefined` for a location with no
 * category at all, or when `categoryStandColor` (the master switch for
 * this whole feature) is unset.
 */
function categoryColor(location: LocationProfile): string | undefined {
  if (!config.categoryStandColor || location.categories.length === 0) return undefined;
  for (const category of location.categories) {
    const override = config.categoryColors[category.name];
    if (override) return override;
  }
  return config.categoryStandColor;
}

type LocalPoint = [number, number];

/**
 * Projects `ring` to local, flat meters — around its own first vertex by
 * default, or around `[originLon, originLat]` when given. An explicit
 * shared origin is what lets `applyStandColoredWalls` compare distances
 * between DIFFERENT stands' rings in one common frame; every other caller
 * just needs one ring's own internal geometry, so they leave it off.
 */
function ringToLocalMeters(ring: [number, number][], originLon?: number, originLat?: number): LocalPoint[] {
  const METERS_PER_DEG_LAT = 111_320;
  const [lon0, lat0] = originLon !== undefined && originLat !== undefined ? [originLon, originLat] : ring[0];
  const metersPerDegLon = METERS_PER_DEG_LAT * Math.cos(lat0 * (Math.PI / 180));
  return ring.map(([lon, lat]): LocalPoint => [(lon - lon0) * metersPerDegLon, (lat - lat0) * METERS_PER_DEG_LAT]);
}

/** Andrew's monotone chain convex hull. */
function convexHull(points: LocalPoint[]): LocalPoint[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: LocalPoint, a: LocalPoint, b: LocalPoint) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower: LocalPoint[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: LocalPoint[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/**
 * Finds the minimum-area rectangle enclosing `hull` via rotating calipers —
 * tried against every hull edge's direction, since a rectangle's minimum
 * bounding box is always flush with one of its own edges. This is what lets
 * a stand that isn't aligned to true north still get a tightly and
 * correctly oriented image, instead of a north-aligned box that's both
 * bigger than the stand and rotated away from it.
 */
function minAreaRect(
  hull: LocalPoint[],
): { width: number; height: number; rotationDeg: number; edgeAngle: number; center: LocalPoint } | null {
  if (hull.length < 3) return null;

  let best: { area: number; width: number; height: number; edgeAngle: number; centerRotated: LocalPoint } | null = null;

  for (let i = 0; i < hull.length; i++) {
    const [x1, y1] = hull[i];
    const [x2, y2] = hull[(i + 1) % hull.length];
    const edgeAngle = Math.atan2(y2 - y1, x2 - x1);
    const cos = Math.cos(-edgeAngle);
    const sin = Math.sin(-edgeAngle);

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y] of hull) {
      const rx = x * cos - y * sin;
      const ry = x * sin + y * cos;
      if (rx < minX) minX = rx;
      if (rx > maxX) maxX = rx;
      if (ry < minY) minY = ry;
      if (ry > maxY) maxY = ry;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height;
    if (!best || area < best.area) {
      best = { area, width, height, edgeAngle, centerRotated: [(minX + maxX) / 2, (minY + maxY) / 2] };
    }
  }
  if (!best) return null;

  // Local (x, y) = (east, north); compass bearing is clockwise from north.
  // The extra +90 corrects for Image3D's actual rotation convention being
  // 90° offset from the plain compass-bearing math, confirmed by testing
  // against the live map (every stand's photo came out rotated a quarter
  // turn from the stand itself until this offset was added).
  const edgeAngleDeg = (best.edgeAngle * 180) / Math.PI;
  const rotationDeg = ((180 - edgeAngleDeg) % 360 + 360) % 360;
  // Rotate the rectangle's own center back out of the caliper-test frame
  // and into the same local-meters frame `hull`'s points are already in.
  const center = rotatePoint(best.centerRotated, best.edgeAngle);
  return { width: best.width, height: best.height, rotationDeg, edgeAngle: best.edgeAngle, center };
}

/**
 * Fits a space's actual footprint — its size AND its orientation — from the
 * outer ring of its floor polygon, so a photo placed on it matches the
 * stand's real rotation instead of always coming out axis-aligned to true
 * north. Stretched to the FULL bounding rectangle, wall to wall — no
 * shrink.
 *
 * For a non-rectangular stand (an L-shape with a notch cut out of one
 * corner, say) that bounding rectangle is necessarily bigger than the
 * stand's real floor — `Image3D` only places flat rectangles, so a photo
 * sized to it will cover whatever's missing from that corner too, unless
 * the source photo itself has that region made transparent (crop/edit the
 * actual `public/stands/<externalId>.*` file for that — see the
 * "F&B AREA HALL 2" fix for an example). An earlier version shrank the
 * rectangle automatically instead, but that fights a properly-prepared
 * transparent photo by leaving a gap between the image and the real
 * walls — better to stretch exactly to the walls and let a well-prepared
 * photo handle its own overflow via transparency.
 *
 * Also returns the rectangle's own true center as a real map coordinate
 * (`longitude`/`latitude`), rather than leaving the caller to anchor on
 * the space itself: `Image3D.add(space, ...)` centers on whatever anchor
 * point the SDK auto-computes for that space, which isn't guaranteed to
 * match this rectangle's own geometric center for an irregular
 * (non-rectangular) polygon — a mismatch there shows up as the whole
 * photo sitting slightly offset from the stand's real walls.
 */
function computeSpaceFootprint(
  space: Space,
): { width: number; height: number; rotation: number; longitude: number; latitude: number } | null {
  const geometry = space.geoJSON.geometry as { type: string; coordinates?: unknown };
  if (geometry.type !== "Polygon") return null;

  const ring = (geometry.coordinates as [number, number][][] | undefined)?.[0];
  if (!ring || ring.length < 3) return null;

  const hull = convexHull(ringToLocalMeters(ring));
  const rect = minAreaRect(hull);
  if (!rect || !Number.isFinite(rect.width) || !Number.isFinite(rect.height) || rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  const [lon0, lat0] = ring[0];
  const [longitude, latitude] = localMetersToLonLat(rect.center, lon0, lat0);
  return { width: rect.width, height: rect.height, rotation: rect.rotationDeg, longitude, latitude };
}

/** Standard ray-casting point-in-polygon test on a [x, y] ring (works for a non-convex polygon, unlike a convex-hull test). */
function pointInPolygon(point: LocalPoint, ring: LocalPoint[]): boolean {
  const [px, py] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Rotates `[x, y]` by `angle` radians around the origin. */
function rotatePoint([x, y]: LocalPoint, angle: number): LocalPoint {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x * cos - y * sin, x * sin + y * cos];
}

/** Inverse of `ringToLocalMeters`: converts a local-meters point back to [lon, lat], relative to the same `[lon0, lat0]` origin. */
function localMetersToLonLat([x, y]: LocalPoint, lon0: number, lat0: number): [number, number] {
  const METERS_PER_DEG_LAT = 111_320;
  const metersPerDegLon = METERS_PER_DEG_LAT * Math.cos(lat0 * (Math.PI / 180));
  return [lon0 + x / metersPerDegLon, lat0 + y / METERS_PER_DEG_LAT];
}

/** Unit vector of `[x, y]`; `[0, 0]` for a zero-length input rather than dividing by zero. */
function normalizeVector([x, y]: LocalPoint): LocalPoint {
  const len = Math.hypot(x, y);
  return len > 0 ? [x / len, y / len] : [0, 0];
}

/**
 * Pushes a closed ring outward by `distance` meters, via a mitered offset
 * at each vertex (the two adjacent edges' averaged outward normal, scaled
 * so the edges themselves land exactly `distance` away — not just the
 * averaged direction, which alone undershoots at any corner sharper than
 * dead straight). Used to make `applyStandColoredWalls`' shapes extend
 * slightly past the stand's exact floor boundary — sized to the boundary
 * exactly, it renders hidden behind the real (thicker) wall geometry
 * everywhere except at a door gap, where there's no wall in front of it.
 * The miter length is capped for a very sharp/reflex corner, a standard
 * safeguard against an extreme spike there.
 */
function offsetRingOutward(ring: LocalPoint[], distance: number): LocalPoint[] {
  const closed = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
  const pts = closed ? ring.slice(0, -1) : ring;
  const m = pts.length;
  if (m < 3) return ring;

  let signedArea = 0;
  for (let i = 0; i < m; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % m];
    signedArea += x1 * y2 - x2 * y1;
  }
  const ccw = signedArea > 0;

  const offset: LocalPoint[] = pts.map((curr, i) => {
    const prev = pts[(i - 1 + m) % m];
    const next = pts[(i + 1) % m];
    const e1 = normalizeVector([curr[0] - prev[0], curr[1] - prev[1]]);
    const e2 = normalizeVector([next[0] - curr[0], next[1] - curr[1]]);
    const n1: LocalPoint = ccw ? [e1[1], -e1[0]] : [-e1[1], e1[0]];
    const n2: LocalPoint = ccw ? [e2[1], -e2[0]] : [-e2[1], e2[0]];
    const avg = normalizeVector([n1[0] + n2[0], n1[1] + n2[1]]);
    const dot = avg[0] * n1[0] + avg[1] * n1[1];
    const scale = distance / Math.max(dot, 0.35);
    return [curr[0] + avg[0] * scale, curr[1] + avg[1] * scale];
  });
  offset.push(offset[0]);
  return offset;
}

/**
 * Decomposes a space's real (possibly non-rectangular) floor polygon into a
 * small set of axis-aligned-to-the-stand rectangles that together tile the
 * whole shape with no overflow — e.g. an L-shaped stand becomes exactly 2
 * rectangles instead of 1 oversized bounding one. Used for the plain color
 * cap (`applyBoxCover`), where placing one extra `Image3D` layer per piece
 * is free of the repeat/seam problems a real photo would have (see
 * `applyFittedPhoto`'s own comment for why photos stay single-rectangle).
 *
 * Works by rotating the polygon to align with its own dominant edge
 * (the same orientation `computeSpaceFootprint` already picks), laying a
 * grid over it from its own vertices' coordinates, keeping only the grid
 * cells whose center actually falls inside the polygon, then merging
 * horizontally-adjacent kept cells in each row into wider rectangles. A
 * plain rectangle stand collapses to exactly one piece, so this is a
 * strict superset of the old single-rectangle behavior, not a regression
 * for the common case.
 */
/** One rectangular piece of a decomposed stand shape — a real map position/size/rotation for one `Image3D` layer. */
interface StandRectPiece {
  longitude: number;
  latitude: number;
  width: number;
  height: number;
  rotation: number;
}

/**
 * Sorts `values` and collapses any that land within `tolerance` of the
 * previous kept value into a single grid line. Real stand geometry has
 * small digitization noise (a wall meant to be perfectly straight often
 * lands a few centimeters off in the source data) — without this, two
 * coordinates a few centimeters apart get treated as two separate grid
 * lines, fragmenting a clean shape into a mess of near-zero-width sliver
 * cells instead of the few rectangles it should actually decompose into.
 */
function clusterCoordinates(values: number[], tolerance = 0.15): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const clustered: number[] = [];
  for (const v of sorted) {
    if (clustered.length === 0 || v - clustered[clustered.length - 1] > tolerance) {
      clustered.push(v);
    }
  }
  return clustered;
}

function decomposeStandRects(space: Space): StandRectPiece[] | null {
  const geometry = space.geoJSON.geometry as { type: string; coordinates?: unknown };
  if (geometry.type !== "Polygon") return null;

  const ring = (geometry.coordinates as [number, number][][] | undefined)?.[0];
  if (!ring || ring.length < 3) return null;

  const [lon0, lat0] = ring[0];
  const localRing = ringToLocalMeters(ring);
  const hull = convexHull(localRing);
  const rect = minAreaRect(hull);
  if (!rect) return null;

  // Rotate the REAL polygon (not the hull, which would smooth over any
  // notch) to the same axis-aligned orientation minAreaRect computed on.
  const rotatedRing = localRing.map((p) => rotatePoint(p, -rect.edgeAngle));

  const xs = clusterCoordinates(rotatedRing.map((p) => p[0]));
  const ys = clusterCoordinates(rotatedRing.map((p) => p[1]));

  type Cell = { x0: number; x1: number; y0: number; y1: number };
  const cells: Cell[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = 0; j < ys.length - 1; j++) {
      const x0 = xs[i];
      const x1 = xs[i + 1];
      const y0 = ys[j];
      const y1 = ys[j + 1];
      if (pointInPolygon([(x0 + x1) / 2, (y0 + y1) / 2], rotatedRing)) cells.push({ x0, x1, y0, y1 });
    }
  }
  if (cells.length === 0) return null;

  // Merge horizontally-adjacent cells within the same row so a plain
  // rectangle (or a shape whose rows don't need splitting) collapses to
  // as few pieces as possible, instead of one Image3D layer per grid cell.
  cells.sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
  const merged: Cell[] = [];
  for (const cell of cells) {
    const prev = merged[merged.length - 1];
    if (prev && prev.y0 === cell.y0 && prev.y1 === cell.y1 && Math.abs(prev.x1 - cell.x0) < 1e-6) {
      prev.x1 = cell.x1;
    } else {
      merged.push({ ...cell });
    }
  }

  return merged.map((r) => {
    const centerRotated: LocalPoint = [(r.x0 + r.x1) / 2, (r.y0 + r.y1) / 2];
    const center = rotatePoint(centerRotated, rect.edgeAngle);
    const [longitude, latitude] = localMetersToLonLat(center, lon0, lat0);
    return {
      longitude,
      latitude,
      width: r.x1 - r.x0,
      height: r.y1 - r.y0,
      rotation: rect.rotationDeg,
    };
  });
}

// Location photo image tracking, and which space each one belongs to — a
// photo sits physically on top of its stand's floor and blocks the click
// raycast from ever reaching the space underneath, so clicks on the photo
// are routed back to its space via this map (see `onSpaceClick`).
const locationPhotoImages: Image3DView[] = [];
const spaceByPhotoImage = new Map<Image3DView, Space>();

/**
 * Renders each location's graphic as a raised "3D case" on its stand: a local
 * `public/stands/<externalId>.jpeg` image when one matches the location's
 * External ID as set in the Mappedin admin — the ID is edited on the Location
 * (Locations → pick one → External ID), never on the Space polygon itself, so
 * that is the value we match (with the Space's own externalId as a fallback
 * for legacy fixtures; see `localStandImageUrl`). The location's own uploaded
 * photo (`LocationProfile.images[0]`) is NOT used here — that's reserved for
 * the detail grid/card photo only. This is also separate from the logo
 * marker (`renderLocationLogos`).
 *
 * Deliberately NOT using the space's native `topTexture`: that maps a fresh
 * 0–1 texture square onto every triangle/quad of the space's own floor mesh
 * independently, so a stand whose floor is more than one face shows the
 * same photo repeated once per face — contained within the stand (no
 * overflow), but still visibly tiled. No pre-processing of the image can
 * fix that, since the repeat count comes from the mesh's own topology, not
 * from the texture. `Image3D` sidesteps this entirely by adding our own
 * single quad instead of relying on the floor's material.
 *
 * The full photo is used as-is (no cropping) — `Image3D` stretches it
 * non-uniformly to exactly fill the given width �— height, so its four
 * corners land on the stand's four (rotated-rectangle) corners with the
 * entire image visible, at the cost of some stretch distortion whenever the
 * photo's own aspect ratio doesn't match the stand's.
 */
/**
 * The stand's local "3D case" image, keyed by the location's External ID as
 * set in the Mappedin admin (Locations → pick one → the External ID field
 * there) — the Space's own `externalId` is only a fallback for legacy
 * fixtures that set it instead. A manifest lookup (see local-assets.ts);
 * `undefined` when no local `public/stands/<externalId>.*` file exists —
 * this image is local-only by design and never falls back to the
 * remotely-uploaded `LocationProfile.images[0]`.
 */
function standExternalId(space: Space, location: LocationProfile): string | undefined {
  return location.externalId || space.externalId || undefined;
}

/**
 * This venue's real numbered stands always have an External ID that STARTS
 * with a digit — "1250", "4100", even a sub-divided "1150 A" still counts
 * as stand 1150 (`parseInt` stops at the first non-digit, unlike `Number`,
 * which would reject the whole string). A named area like "Hall 1" or
 * "WALKWAY HALL 3" never starts with a digit, so this returns `undefined`
 * for those — the box/cover/3D-case system (`renderLocationPhotos`) and the
 * range tint (`standRangeColor`) both use this to tell an actual stand
 * apart from a hall/lobby/walkway that merely has *some* External ID set.
 */
function standNumericId(externalId: string | undefined): number | undefined {
  if (!externalId) return undefined;
  const num = parseInt(externalId, 10);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * Whether a stand with no photo gets the box treatment: any numbered stand,
 * plus a named area (e.g. "VR AREA") listed by name in
 * VITE_STAND_COLOR_RANGES — naming it there marks it as a real stand, unlike
 * a hall/walkway that merely has some External ID set.
 */
function isBoxableStand(externalId: string): boolean {
  return standNumericId(externalId) !== undefined || externalId in config.standColorRanges.ids;
}

async function localStandImageUrl(externalId: string): Promise<string | undefined> {
  return resolveLocalAsset("stands", externalId, undefined);
}

/**
 * Real photos can be switched off entirely with VITE_SHOW_LOCATION_PHOTOS=false
 * (see `config.showLocationPhotos`) — no stand is checked for a local photo
 * and no photo texture is ever requested from the browser. A real photo is
 * checked for ANY entry with an External ID at all, numeric or not — a
 * named one like "F&B AREA HALL 2" gets its photo shown exactly like a
 * plain-numbered stand would, as long as a matching
 * `public/stands/<externalId>.*` file exists. Only the FALLBACK color cap
 * (when `standAsBox` is on and there's no photo) is restricted to numbered
 * stands, plus named ones listed in VITE_STAND_COLOR_RANGES (see
 * `isBoxableStand`): a huge named area like "Hall 1" has no
 * photo of its own either, but unlike "F&B AREA HALL 2" it was never meant
 * to get boxed/covered at all, and there's no per-entry signal (like an
 * uploaded photo) to tell the two apart other than the numbering
 * convention this venue's real stands actually follow. That fallback cap
 * is itself skipped when `showStandColoredWalls` is also on and the stand
 * has a matching range color — `applyStandColorRanges`'s own colored wall
 * `Shape` already gives that stand a complete, seamless top+walls, so
 * adding this flat cap too would just leave a thin gap between the two,
 * exposing a sliver of the real (uncolored) wall in between.
 */
export async function renderLocationPhotos(
  mapView: MapView,
  entries: { space: Space; location: LocationProfile; photoUrl?: string }[],
): Promise<void> {
  if (!config.showLocationPhotos && !config.standAsBox) return;
  for (const entry of entries) {
    const externalId = standExternalId(entry.space, entry.location);
    if (externalId === undefined) continue;
    const photoUrl = config.showLocationPhotos ? await localStandImageUrl(externalId) : undefined;
    if (!photoUrl && (!config.standAsBox || !isBoxableStand(externalId))) continue;
    if (photoUrl) {
      const footprint = computeSpaceFootprint(entry.space);
      if (footprint) applyFittedPhoto(mapView, entry.space, photoUrl, footprint);
    } else {
      // A stand's own `standColorRanges` color wins over the fixed
      // `standBoxCoverColor` fallback, so a boxed stand with no photo still
      // reads in its range's color on top, matching its already-tinted floor.
      const rangeColor = standRangeColor(externalId);
      // A stand that's about to get the colored wall Shape (`applyStandColorRanges`)
      // already has a complete, seamless colored top+walls from that alone —
      // this flat Image3D cap would just sit a hair below its top face,
      // exposing a thin sliver of the real (uncolored) wall in the gap
      // between the two. Skip it entirely rather than double up.
      if (config.showStandColoredWalls && rangeColor) continue;
      applyBoxCover(mapView, entry.space, rangeColor || config.standBoxCoverColor);
    }
  }
}

const FIT_SCALE = 1.0; // the graphic covers the stand's full footprint — no bare floor margin around it

const PHOTO_VERTICAL_OFFSET = 0.2; // height of the photo above the stand floor

/**
 * `anchor` is the actual Image3D target (either the space itself, or a
 * Coordinate for one piece of a multi-rectangle decomposition — see
 * `decomposeStandRects`); `space` is always the real stand it belongs to,
 * tracked separately so a click on ANY piece still routes back to the
 * right space via `onSpaceClick`, regardless of which coordinate the piece
 * was actually anchored to.
 */
function addImageLayer(
  mapView: MapView,
  anchor: Space | Coordinate,
  space: Space,
  url: string,
  footprint: { width: number; height: number; rotation: number },
  verticalOffset: number,
  scale: number,
  maxImageSize?: number,
): void {
  try {
    const image = mapView.Image3D.add(anchor, url, {
      width: footprint.width * scale,
      height: footprint.height * scale,
      rotation: footprint.rotation,
      verticalOffset,
      maxImageSize,
      // Interactive so the click still resolves to something (picked up as
      // `event.images` in `onSpaceClick`) instead of being silently
      // swallowed by a layer sitting in front of the space's own floor.
      interactive: true,
    });
    if (image) {
      locationPhotoImages.push(image);
      spaceByPhotoImage.set(image, space);
    }
  } catch (err) {
    console.warn("[renderLocationPhotos] Failed to add Image3D:", err);
  }
}

/** Shared by a stand's real photo and its fallback color cap, so both "sit" at the same height on a boxed stand. */
function standTopLayerPlacement(): { verticalOffset: number; scale: number } {
  const onCover = config.standPhotoPosition === "cover";
  return {
    verticalOffset: onCover ? config.standBoxHeight : PHOTO_VERTICAL_OFFSET,
    scale: onCover ? config.standBoxCoverScale : FIT_SCALE,
  };
}

/**
 * Every stand's photo — rectangular or not — gets one `Image3D` layer,
 * stretched to the shrink-to-fit footprint (see `computeSpaceFootprint`).
 *
 * A multi-piece version (splitting a non-rectangular stand's photo into
 * one cropped `Image3D` layer per `decomposeStandRects` piece, like
 * `applyBoxCover` already does for the plain fallback color) was tried and
 * reverted: it depends on an assumption about which map axis corresponds
 * to which direction in the source image, and that assumption turned out
 * wrong on a real L-shaped stand — pieces showed duplicated content
 * instead of reconstructing the photo. A flat color has no directional
 * content to get backwards, so `applyBoxCover` keeps the multi-piece
 * version safely; a real photo does, so it stays single-rectangle until
 * that mapping can be verified against the live renderer and fixed.
 */
function applyFittedPhoto(
  mapView: MapView,
  space: Space,
  url: string,
  footprint: { width: number; height: number; rotation: number; longitude: number; latitude: number },
): void {
  // Image3D's default 1024px cap made the photo look blurry once stretched
  // over a whole stand and viewed up close — raised for sharper results at
  // the cost of a bit more VRAM per stand.
  const { verticalOffset, scale } = standTopLayerPlacement();
  // Anchored to the footprint's own computed center rather than the space
  // itself — see `computeSpaceFootprint`'s own comment for why.
  const coordinate = new Coordinate({ latitude: footprint.latitude, longitude: footprint.longitude, floorId: space.floor.id });
  addImageLayer(mapView, coordinate, space, url, footprint, verticalOffset, scale, 2048);
}

const boxCoverDataUriByColor = new Map<string, string>();

/** A tiny solid-fill SVG for `color`, generated once per color and reused for every boxed stand without its own photo. */
function getBoxCoverDataUri(color: string): string {
  let uri = boxCoverDataUriByColor.get(color);
  if (!uri) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="4" height="4"><rect width="4" height="4" fill="${color}"/></svg>`;
    uri = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    boxCoverDataUriByColor.set(color, uri);
  }
  return uri;
}

/**
 * `color` is the stand's own `standColorRanges` color when it has one
 * (passed in by `renderLocationPhotos`), falling back to the fixed
 * `standBoxCoverColor` otherwise. Reading the wall's own state color here
 * (via mapView.getState(WALLS.Interior)) looks appealing but doesn't
 * actually produce a visual match: Image3D renders its flat quad lit
 * differently than the native wall/floor geometry, and a boxed stand's
 * vertical wall FACES look darker than its own flat top purely from 3D
 * shading (light hitting a vertical surface at a different angle than a
 * horizontal one) — not from a different material color. So there is no
 * single color value that can be copied to make the cap match both the
 * shaded wall sides and the flat floor at once.
 *
 * Unlike a real photo (`applyFittedPhoto`, stuck at one bounding
 * rectangle — see its own comment for why), a flat color has no
 * repeat/seam problem when split into multiple pieces, so this uses
 * `decomposeStandRects` to add one small `Image3D` layer per piece of the
 * stand's REAL shape instead of one oversized rectangle — a non-rectangular
 * stand's cap then exactly follows its own outline with no overflow. Each
 * piece is anchored to its own Coordinate rather than the space itself
 * (Image3D can only center on one point per call), but every piece still
 * maps back to `space` in `spaceByPhotoImage` for click routing.
 */
function applyBoxCover(mapView: MapView, space: Space, color: string): void {
  const pieces = decomposeStandRects(space);
  if (!pieces) return;
  const { verticalOffset, scale } = standTopLayerPlacement();
  const url = getBoxCoverDataUri(color);
  for (const piece of pieces) {
    const coordinate = new Coordinate({
      latitude: piece.latitude,
      longitude: piece.longitude,
      floorId: space.floor.id,
    });
    addImageLayer(mapView, coordinate, space, url, piece, verticalOffset, scale);
  }
}

export function clearLocationPhotos(mapView: MapView): void {
  for (const img of locationPhotoImages) {
    try { mapView.Image3D.remove(img); } catch { }
    spaceByPhotoImage.delete(img);
  }
  locationPhotoImages.length = 0;
}

/**
 * Attaches a pulsing "You Are Here" radar marker at `anchor` — either the
 * kiosk's space in general (its centroid, used when idle/no route is
 * active) or, once a route is calculated, the route's actual starting
 * coordinate. Anchoring to the space centroid while a route is showing
 * looks wrong whenever that centroid sits away from the room's
 * door/aisle edge the route line actually starts from — see
 * `calculateAndShowRoute`, which re-calls this with that coordinate.
 */
export function addKioskMarker(mapView: MapView, anchor: MapTarget): void {
  if (kioskMarker) {
    try { mapView.Markers.remove(kioskMarker); } catch { }
    kioskMarker = null;
  }
  try {
    kioskMarker = mapView.Markers.add(
      anchor,
      `<div class="kiosk-radar-pin" aria-label="You are here">
        <span class="radar-wave"></span>
        <img class="radar-icon" src="/you-are-here-icon.webp" alt="" />
        <span class="radar-label">You Are Here</span>
      </div>`,
      // The icon's centre is the kiosk point (see `.kiosk-radar-pin` in style.css).
      { rank: "always-visible", placement: "center" },
    );
  } catch (err) {
    console.warn("Failed to place kiosk marker:", err);
  }
}

/** Fades "You Are Here" out/in — hidden while the route intro plays. */
function setKioskMarkerHidden(hidden: boolean): void {
  kioskMarker?.contentEl.querySelector(".kiosk-radar-pin")?.classList.toggle("is-hidden", hidden);
}

// Emergency exit markers — one red exit sign per exit door
const exitMarkers: Marker[] = [];

/**
 * Puts a red "exit" sign on every emergency exit door (see
 * `buildExitDirectory`), always visible. Tapping one calls `onTap` — main.ts
 * makes that exit the destination. Only the sign itself takes the tap (as
 * with stand logos), so the marker box never swallows taps on the map.
 */
export function renderExitMarkers(mapView: MapView, exits: ExitAnchor[], onTap: (exit: ExitAnchor) => void): void {
  clearExitMarkers(mapView);
  for (const exit of exits) {
    try {
      const marker = mapView.Markers.add(
        exit.target,
        `<div class="exit-marker" aria-label="${exit.name}" title="${exit.name}">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </div>`,
        { rank: "always-visible", interactive: false },
      );
      marker.contentEl.querySelector(".exit-marker")?.addEventListener("click", (event) => {
        event.stopPropagation();
        onTap(exit);
      });
      exitMarkers.push(marker);
    } catch (err) {
      console.warn("Failed to place exit marker:", exit.name, err);
    }
  }
}

/**
 * The emergency exit with the shortest walking route from `from` — real
 * route distance, not straight-line, so a wall between you and an exit
 * counts. Exits with no route (a door not joined to the walkways) are
 * skipped. Null when none can be reached.
 */
export async function findNearestExit(
  mapView: MapView,
  from: MapTarget,
  exits: ExitAnchor[],
): Promise<{ exit: ExitAnchor; distanceMeters: number } | null> {
  const results = await Promise.all(
    exits.map(async (exit) => {
      const directions = await mapView.getDirections(from, exit.target, { accessible: false }).catch(() => undefined);
      return directions ? { exit, distanceMeters: directions.distance } : null;
    }),
  );
  let best: { exit: ExitAnchor; distanceMeters: number } | null = null;
  for (const r of results) if (r && (!best || r.distanceMeters < best.distanceMeters)) best = r;
  return best;
}

export function clearExitMarkers(mapView: MapView): void {
  for (const m of exitMarkers) {
    try { mapView.Markers.remove(m); } catch { }
  }
  exitMarkers.length = 0;
}

// Kiosk pointer marker tracking — one icon per physical kiosk stand across the venue
const kioskPointerMarkers: Marker[] = [];

/**
 * Places a small, static "kiosk stand" icon at each of `targets` — marking
 * every physical self-service kiosk unit across the venue (see
 * VITE_ALL_KIOSK_EXTERNAL_IDS), shown alongside (not instead of) this
 * device's own pulsing "You Are Here" marker. `placement: "top"` stacks an
 * icon above whichever target also happens to be this device's own
 * "You Are Here" anchor, instead of directly overlapping it.
 *
 * Deliberately separate from `addKioskMarker`/`kioskMarker`: these are set
 * once, at boot, from the venue's full kiosk roster — unlike "You Are Here",
 * they're never re-anchored to a live GPS fix or a shared route's starting
 * point, since those don't mark an actual kiosk stand.
 */
export function renderKioskPointerMarkers(mapView: MapView, targets: (Space | PointOfInterest)[]): void {
  clearKioskPointerMarkers(mapView);
  for (const target of targets) {
    try {
      const marker = mapView.Markers.add(
        target,
        `<div class="kiosk-pointer-pin" aria-label="Kiosk stand">
          <img class="kiosk-pointer-icon" src="/kiosk-pointer-icon.png" alt="" />
        </div>`,
        { rank: "always-visible", placement: "top", interactive: false },
      );
      if (marker) kioskPointerMarkers.push(marker);
    } catch (err) {
      console.warn("Failed to place kiosk pointer marker:", err);
    }
  }
}

export function clearKioskPointerMarkers(mapView: MapView): void {
  for (const m of kioskPointerMarkers) {
    try { mapView.Markers.remove(m); } catch { }
  }
  kioskPointerMarkers.length = 0;
}

/**
 * The route currently on screen, indexed for live-location snapping (see
 * `startLiveLocationTracking`): the walker's "You Are Here" dot is forced
 * to travel along this line rather than follow raw GPS, which indoors can
 * wander tens of metres sideways. `progress` is how far along (metres from
 * the start) the dot has got — it only ever moves forward.
 */
let liveRoute: { path: Coordinate[]; cumulative: number[]; progress: number; lastFixAt: number } | null = null;

/** Fastest the dot may advance along the route, in m/s — a brisk walk, so a bad GPS jump can't teleport it ahead. */
const LIVE_WALK_SPEED_MPS = 2.5;
/** A fix further than this from the route line is ignored (the visitor is nowhere near it, or the fix is junk). */
const LIVE_MAX_OFF_ROUTE_METERS = 40;

function setLiveRoute(path: Coordinate[]): void {
  if (path.length < 2) {
    liveRoute = null;
    return;
  }
  const cumulative = [0];
  for (let i = 1; i < path.length; i++) cumulative.push(cumulative[i - 1] + metersBetween(path[i - 1], path[i]));
  liveRoute = { path, cumulative, progress: 0, lastFixAt: 0 };
}

/** Closest point on the route to `point`, searching only between `sMin` and `sMax` metres along it — so a route that doubles back can't pull the dot onto a later leg. */
function projectOntoRoute(
  route: NonNullable<typeof liveRoute>,
  point: Coordinate,
  sMin: number,
  sMax: number,
): { s: number; distance: number } | null {
  let best: { s: number; distance: number } | null = null;
  const cosLat = Math.cos((point.latitude * Math.PI) / 180);
  const toXY = (c: Coordinate): [number, number] => [
    (((c.longitude - point.longitude) * Math.PI) / 180) * cosLat * 6371000,
    (((c.latitude - point.latitude) * Math.PI) / 180) * 6371000,
  ];
  for (let i = 1; i < route.path.length; i++) {
    if (route.cumulative[i] < sMin || route.cumulative[i - 1] > sMax) continue;
    const [ax, ay] = toXY(route.path[i - 1]);
    const [bx, by] = toXY(route.path[i]);
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2));
    const distance = Math.hypot(ax + dx * t, ay + dy * t);
    if (!best || distance < best.distance) {
      best = { s: route.cumulative[i - 1] + (route.cumulative[i] - route.cumulative[i - 1]) * t, distance };
    }
  }
  return best;
}

export type LiveLocationErrorKind = "denied" | "unavailable";

export interface LiveLocationCallbacks {
  onFix?: (accuracyMeters: number) => void;
  onError?: (kind: LiveLocationErrorKind) => void;
}

/**
 * Continuously tracks the device's real GPS position and glides the
 * existing "You Are Here" marker (`addKioskMarker`) to match, converting
 * each fix into a map {@link Coordinate} on whichever floor is currently
 * displayed — GPS carries no floor/elevation signal, so it can't tell
 * which floor the device is actually on.
 *
 * Only meant to run on a visitor's own phone (opened via the QR-shared
 * route link — see `restoreSharedRoute` in main.ts): the physical kiosk
 * itself never moves, so it keeps its fixed configured location instead of
 * this.
 *
 * Fixes worse than `maxAccuracyMeters` are ignored outright — indoors, raw
 * GPS can easily report 50m+ of error, which would otherwise send the dot
 * jumping between unrelated stands.
 *
 * @returns A function that stops the tracking (same as calling {@link stopLiveLocationTracking}).
 */
export function startLiveLocationTracking(
  mapView: MapView,
  callbacks: LiveLocationCallbacks = {},
  maxAccuracyMeters = 40,
): () => void {
  stopLiveLocationTracking();

  if (!("geolocation" in navigator)) {
    callbacks.onError?.("unavailable");
    return stopLiveLocationTracking;
  }

  liveLocationWatchId = navigator.geolocation.watchPosition(
    (position) => {
      const floorId = mapView.currentFloor?.id;
      if (!floorId) return;
      if (position.coords.accuracy > maxAccuracyMeters) return;

      let coordinate = new Coordinate({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        floorId,
      });

      // With a route on screen, don't show raw GPS: snap the fix onto the
      // route and let the dot only creep forward along it at walking speed.
      // Sideways wobble, a lagging fix or a one-off jump then never shows —
      // the dot simply walks the path. No route (or too far from it): raw GPS as before.
      if (liveRoute) {
        const now = performance.now();
        const dt = liveRoute.lastFixAt ? Math.min(5, (now - liveRoute.lastFixAt) / 1000) : 1;
        liveRoute.lastFixAt = now;
        const hit = projectOntoRoute(liveRoute, coordinate, liveRoute.progress - 5, liveRoute.progress + 40);
        if (!hit || hit.distance > LIVE_MAX_OFF_ROUTE_METERS) return;
        const maxAdvance = LIVE_WALK_SPEED_MPS * dt + 0.5;
        liveRoute.progress = Math.min(Math.max(liveRoute.progress, hit.s), liveRoute.progress + maxAdvance);
        coordinate = pointAlongRoute(liveRoute.path, liveRoute.cumulative, liveRoute.progress);
      }

      if (kioskMarker) {
        void mapView.Markers.animateTo(kioskMarker, coordinate, { duration: 900 });
      } else {
        addKioskMarker(mapView, coordinate);
      }
      callbacks.onFix?.(Math.round(position.coords.accuracy));
    },
    (err) => {
      callbacks.onError?.(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable");
    },
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
  );

  return stopLiveLocationTracking;
}

/** Stops any active `startLiveLocationTracking` watch, leaving the marker at its last position. */
export function stopLiveLocationTracking(): void {
  if (liveLocationWatchId !== null) {
    navigator.geolocation.clearWatch(liveLocationWatchId);
    liveLocationWatchId = null;
  }
}

/** Red drop pin — its tip is the bottom-centre of the SVG, the exact map point it marks. */
const DEST_PIN_SVG = `<svg viewBox="0 0 24 32" width="28" height="36" fill="none">
  <path d="M12 0C5.37 0 0 5.37 0 12c0 8.5 12 20 12 20s12-11.5 12-20c0-6.63-5.37-12-12-12z" fill="#ea4335"/>
  <circle cx="12" cy="11" r="4.5" fill="#ffffff"/>
</svg>`;

let destinationTarget: MapTarget | null = null;
let destinationName = "";
/** The route intro currently allowed to move the destination pin (see `playRouteIntro`). */
let pinIntroRun = 0;

/**
 * Attaches a Google Maps-style red drop pin at the destination stand, with
 * the stand's logo (if it has one) shown above it on arrival. The
 * name sits above the pin and `placement: "top"` puts the element's
 * bottom-centre — the pin's tip — exactly on the target's anchor point.
 * Calling it again for the same destination keeps the existing pin (and
 * its arrival effect), e.g. on a silent live-location route refresh.
 */
export function setDestinationMarker(
  mapView: MapView,
  target: MapTarget,
  name: string,
  logoUrl?: string,
): void {
  if (destinationMarker && destinationTarget === target && destinationName === name) return;
  clearDestinationMarker(mapView);
  // The stand's own logo marker sits on the same anchor and loses the
  // collision to this always-visible pin, so the pin carries the logo itself
  // — revealed once the route intro parks it on the stand (`.is-arrived`).
  const logoHtml =
    logoUrl && config.showLocationLogos
      ? `<div class="pin-logo shape-${config.logoShape}"><img src="${logoUrl}" alt="" /></div>`
      : "";
  try {
    destinationMarker = mapView.Markers.add(
      target,
      `<div class="dest-map-pin" aria-label="Destination: ${name}">
        ${logoHtml}
        <div class="pin-title">${name}</div>
        <div class="pin-marker-head"><span class="pin-pulse"></span>${DEST_PIN_SVG}</div>
      </div>`,
      { rank: "always-visible", placement: "top", interactive: false },
    );
    // Tapping the arrival logo opens/focuses the stand, same as tapping the stand.
    if (logoHtml && "__type" in target && target.__type === "space") bindLogoClick(destinationMarker, ".pin-logo img", target);
    destinationTarget = target;
    destinationName = name;
  } catch (err) {
    console.warn("Failed to place destination marker:", err);
  }
}

export function clearDestinationMarker(mapView: MapView): void {
  stopDestinationGlow(mapView);
  if (destinationMarker) {
    try { mapView.Markers.remove(destinationMarker); } catch { }
    destinationMarker = null;
  }
  destinationTarget = null;
  destinationName = "";
}

/** Destination arrival effect — set in .env (see config.destHighlight*). */
const DEST_GLOW_COLOR = config.destHighlightColor; // VITE_DEST_HIGHLIGHT_COLOR
const DEST_GLOW_HEIGHT = config.destHighlightHeight; // VITE_DEST_HIGHLIGHT_HEIGHT
const DEST_GLOW_RISE_MS = config.destHighlightRiseMs; // VITE_DEST_HIGHLIGHT_RISE_MS
/** `standWall`: the stand's own coloured box, hidden while the blue block stands in for it. */
let destinationGlow: { shapes: Shape[]; standWall: Shape | undefined; frame: number } | null = null;

/**
 * Arrival effect on the destination itself: a blue wall,
 * hugging its (or the hall's) footprint, rises out of the floor and stays up, so the whole
 * stand reads as "this one". Drawn as its own shape rather than recolouring the
 * space, since a stand's photo/colour cap would otherwise cover the tint.
 * A POI or bare coordinate has no footprint — its pin pulse is the effect.
 */
function startDestinationGlow(mapView: MapView, target: MapTarget): void {
  stopDestinationGlow(mapView);
  const space = target as Partial<Space>;
  const geometry = space.geoJSON?.geometry as { type: string; coordinates?: unknown } | undefined;
  if (!geometry || !space.floor) return;
  const polygons =
    geometry.type === "Polygon"
      ? [geometry.coordinates as [number, number][][]]
      : geometry.type === "MultiPolygon"
        ? (geometry.coordinates as [number, number][][][])
        : [];

  // A stand with its own coloured wall box (no photo) rises as one solid
  // block: its box is hidden and a blue block of the same footprint
  // takes its place (a shape's top colour can't be changed after creation,
  // so recolouring the box itself would leave its top in the old colour).
  // A stand with a photo keeps the photo visible: only a hollow band rises around it.
  const standWall = space.id ? standWallShapeBySpaceId.get(space.id) : undefined;

  const shapes: Shape[] = [];
  for (const polygon of polygons) {
    const ring = polygon?.[0];
    if (!ring || ring.length < 3) continue;
    const [lon0, lat0] = ring[0];
    const local = ringToLocalMeters(ring, lon0, lat0);
    const outer = offsetRingOutward(local, 0.45).map((p) => localMetersToLonLat(p, lon0, lat0));
    const inner = offsetRingOutward(local, 0.1).map((p) => localMetersToLonLat(p, lon0, lat0)).reverse();
    const featureCollection: FeatureCollection<Polygon> = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: { type: "Polygon", coordinates: standWall ? [outer] : [outer, inner] } as unknown as Polygon,
        },
      ],
    };
    try {
      const shape = mapView.Shapes.add(
        featureCollection,
        { color: DEST_GLOW_COLOR, topColor: DEST_GLOW_COLOR, height: 0.01, altitude: 0, opacity: 0.95, outline: false },
        space.floor,
      );
      if (shape) {
        // Same reason as the coloured walls: don't swallow taps on the stand.
        mapView.updateState(shape, { interactive: "pass-through" });
        shapes.push(shape);
      }
    } catch (err) {
      console.warn("[startDestinationGlow] Failed to add glow shape:", err);
    }
  }
  if (shapes.length === 0) return;
  if (standWall) {
    try { mapView.updateState(standWall, { visible: false }); } catch { }
  }

  const glow = { shapes, standWall, frame: 0 };
  destinationGlow = glow;
  const startedAt = performance.now();
  const tick = (now: number) => {
    if (destinationGlow !== glow) return;
    const t = Math.min(1, (now - startedAt) / DEST_GLOW_RISE_MS);
    // Ease-out with a small overshoot, so the wall springs up and settles.
    const c = 1.4;
    const eased = 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
    for (const shape of shapes) {
      try {
        mapView.updateState(shape, { height: Math.max(0.01, DEST_GLOW_HEIGHT * eased) });
      } catch { }
    }
    if (t < 1) glow.frame = requestAnimationFrame(tick);
  };
  glow.frame = requestAnimationFrame(tick);
}

function stopDestinationGlow(mapView: MapView): void {
  if (!destinationGlow) return;
  cancelAnimationFrame(destinationGlow.frame);
  for (const shape of destinationGlow.shapes) {
    try { mapView.Shapes.remove(shape); } catch { }
  }
  if (destinationGlow.standWall) {
    try { mapView.updateState(destinationGlow.standWall, { visible: true }); } catch { }
  }
  destinationGlow = null;
}

/**
 * Removes the hover tint entirely. Mappedin replaces a geometry with its
 * hoverColor while the pointer is over it (applied to both faces), so the
 * only way to make hovering invisible — while keeping interactions/click
 * working — is to set each space's hoverColor to its own current color.
 */
function syncHoverColor(mapView: MapView, space: Space): void {
  const color = mapView.getState(space)?.color;
  if (color === undefined) return;
  mapView.updateState(space, { hoverColor: color });
}

/**
 * Enables space interactivity (click/hover events) with no visible hover tint.
 */
export function enableInteractivity(mapView: MapView, mapData: MapData): void {
  const spaces = mapData.getByType("space");
  for (const space of spaces) {
    mapView.updateState(space, { interactive: true });
    syncHoverColor(mapView, space);
  }
}

/**
 * Re-syncs each space's hover color to its current color after a theme switch.
 */
export function updateHoverColors(mapView: MapView, mapData: MapData): void {
  const spaces = mapData.getByType("space");
  for (const space of spaces) {
    syncHoverColor(mapView, space);
  }
}

/** One stand's own floor outline, cached in local meters for fast point-in-polygon hit tests. */
interface StandHitRing {
  space: Space;
  floorId: string;
  ring: LocalPoint[];
  lon0: number;
  lat0: number;
}

let standHitRings: StandHitRing[] = [];

/**
 * Caches every stand's real floor outline for `standAtCoordinate`'s
 * coordinate fallback. The polygon (not the convex hull) is what matters
 * here — an L-shaped stand's two arms both have to resolve, which a hull
 * test would swallow.
 */
export function setStandHitRings(entries: { space: Space }[]): void {
  standHitRings = [];
  for (const entry of entries) {
    const geometry = entry.space.geoJSON.geometry as { type: string; coordinates?: unknown };
    if (geometry.type !== "Polygon") continue;
    const ring = (geometry.coordinates as [number, number][][] | undefined)?.[0];
    if (!ring || ring.length < 3) continue;
    const [lon0, lat0] = ring[0];
    standHitRings.push({ space: entry.space, floorId: entry.space.floor.id, ring: ringToLocalMeters(ring, lon0, lat0), lon0, lat0 });
  }
}

/**
 * Resolves a raw pointer coordinate to the stand whose own floor it lands
 * on, ignoring every 3D overlay. This is the last-resort fallback for
 * boxed stands: their cap/photo is a floating quad raised to
 * `standBoxHeight` and their colored wall is an extruded `Shape`, so a tap
 * on the stand's visible box top hits neither the space polygon below nor
 * (when the raycast stops on the wall) anything interactive at all. The
 * click payload always carries the ground coordinate under the pointer, so
 * testing that against the real outlines still names the right stand.
 */
function standAtCoordinate(coordinate: Coordinate | undefined): Space | undefined {
  if (!coordinate || standHitRings.length === 0) return undefined;
  // The click payload only fills `floorId` when a floor group container was
  // actually hit, so it's legitimately `undefined` for a tap that landed on
  // an overlay. Skip the floor filter in that case rather than reject every
  // candidate — stand outlines never overlap, so the first ring containing
  // the point is still the right one.
  const floorId = coordinate.floorId;
  for (const entry of standHitRings) {
    if (floorId !== undefined && entry.floorId !== floorId) continue;
    const METERS_PER_DEG_LAT = 111_320;
    const metersPerDegLon = METERS_PER_DEG_LAT * Math.cos(entry.lat0 * (Math.PI / 180));
    const point: LocalPoint = [
      (coordinate.longitude - entry.lon0) * metersPerDegLon,
      (coordinate.latitude - entry.lat0) * METERS_PER_DEG_LAT,
    ];
    if (pointInPolygon(point, entry.ring)) return entry.space;
  }
  return undefined;
}

export function onSpaceClick(
  mapView: MapView,
  callback: (space: Space) => void,
): void {
  standClickHandler = callback;
  mapView.on("click", (event: TClickPayload) => {
    // A stand's own photo (if it has one) sits on top of its floor and
    // blocks the click from reaching the space directly, so fall back to
    // the space that photo belongs to.
    const direct = event?.spaces?.[0];
    const fromImage = direct ? undefined : event?.images?.[0] ? spaceByPhotoImage.get(event.images[0]) : undefined;
    // Neither resolved: an overlay (boxed cover, colored wall) stopped the
    // raycast. Fall back to the ground coordinate under the pointer.
    const fromCoordinate = direct || fromImage ? undefined : standAtCoordinate(event?.coordinate);
    const space = direct ?? fromImage ?? fromCoordinate;
    console.log(
      `[onSpaceClick] ${space ? `space=${space.id}` : "UNRESOLVED"} ` +
      `(spaces=${event?.spaces?.length ?? 0} images=${event?.images?.length ?? 0} ` +
      `shapes=${event?.shapes?.length ?? 0} path=${direct ? "space" : fromImage ? "image" : fromCoordinate ? "coordinate" : "none"})`,
    );
    if (space) callback(space);
  });
}

export interface RouteFound {
  status: "found";
  directions: Directions;
  distanceMeters: number;
  steps: number;
  etaMinutes: number;
}
export type RouteResult = RouteFound | { status: "same-stand" } | { status: "no-route" };

let gradientPaths: Path[] = [];

/**
 * Interpolates two hex colors at `t` (0–1) and returns a CSS color string.
 */
function lerpColor(a: string, b: string, t: number): string {
  const parse = (hex: string) => {
    const h = hex.replace("#", "");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const bl = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${bl})`;
}

/**
 * Interpolates across a sequence of colors using `t` in [0, 1].
 */
function lerpColorSequence(colors: readonly string[], t: number): string {
  if (colors.length === 1) return colors[0];
  const clamped = Math.min(1, Math.max(0, t));
  const scaled = clamped * (colors.length - 1);
  const idx = Math.min(colors.length - 2, Math.floor(scaled));
  return lerpColor(colors[idx], colors[idx + 1], scaled - idx);
}

/**
 * Resamples a polyline into `numPoints` points spaced evenly by cumulative
 * distance along the route (not by original vertex index). A short route
 * with only 2-3 waypoints and a long route with a dozen turns both end up
 * with the same number of evenly-spaced points, which is what makes the
 * gradient below look smooth regardless of how many turns the route has.
 */
function resampleByDistance(coords: Coordinate[], numPoints: number): Coordinate[] {
  if (coords.length < 2) return coords;

  const dist = (a: Coordinate, b: Coordinate) => Math.hypot(b.latitude - a.latitude, b.longitude - a.longitude);

  const cumulative = [0];
  for (let i = 1; i < coords.length; i++) {
    cumulative.push(cumulative[i - 1] + dist(coords[i - 1], coords[i]));
  }
  const total = cumulative[cumulative.length - 1];
  if (total === 0) return coords;

  const resampled: Coordinate[] = [];
  let segIdx = 0;
  for (let i = 0; i <= numPoints; i++) {
    const target = (i / numPoints) * total;
    while (segIdx < cumulative.length - 2 && cumulative[segIdx + 1] < target) segIdx++;
    const segStart = cumulative[segIdx];
    const segEnd = cumulative[segIdx + 1];
    const t = segEnd > segStart ? (target - segStart) / (segEnd - segStart) : 0;
    const a = coords[segIdx];
    const b = coords[segIdx + 1];
    resampled.push(
      new Coordinate({
        latitude: a.latitude + (b.latitude - a.latitude) * t,
        longitude: a.longitude + (b.longitude - a.longitude) * t,
        floorId: a.floorId,
      }),
    );
  }
  return resampled;
}

/**
 * Draws gradient-colored path segments along the route, resampled at even
 * distance intervals (see `resampleByDistance`). Each chunk gets its own
 * flat color, blended across the sequence to approximate a continuous
 * gradient — Mappedin's `Paths` API doesn't support a genuine multi-stop
 * gradient on a single path.
 *
 * Color resolution (~1 step per 0.35m) is deliberately fine so each color
 * jump is too small to notice — with only a handful of slabs, each jump is
 * big enough to look like a "brick".
 *
 * Arrows are placed on a handful of longer runs (~3m each) rather than a
 * separate overlay path on top of the route: a thin overlay is its own
 * visibly distinct line (the API has no way to draw arrows without also
 * drawing a colored line under them, and colors have no alpha channel to
 * hide it), and enabling arrows on the hairline-thin gradient slabs makes
 * the animation restart every fraction of a meter, looking tiny and
 * stuttery. An arrow run reuses the same width/color/height as the route
 * itself — it replaces a stretch of gradient slabs rather than stacking on
 * top of them — so arrows render at full route width with no second line.
 */
function drawGradientPath(mapView: MapView, coords: Coordinate[], distanceMeters: number): void {
  clearGradientPaths(mapView);
  const colorSteps = Math.min(140, Math.max(24, Math.round(distanceMeters / 0.35)));
  const points = resampleByDistance(coords, colorSteps);
  const metersPerStep = distanceMeters / colorSteps;

  const arrowRunCount = Math.min(5, Math.max(2, Math.round(distanceMeters / 8)));
  const arrowRunSteps = Math.max(1, Math.min(colorSteps, Math.round(3 / metersPerStep)));
  const arrowRanges = Array.from({ length: arrowRunCount }, (_, k) => {
    const center = Math.round(((k + 0.5) / arrowRunCount) * colorSteps);
    const start = Math.max(0, Math.min(colorSteps - arrowRunSteps, center - Math.floor(arrowRunSteps / 2)));
    return [start, start + arrowRunSteps] as const;
  });

  let i = 0;
  while (i < colorSteps) {
    const range = arrowRanges.find(([start, end]) => i >= start && i < end);
    const [start, end] = range ?? [i, i + 1];
    const chunk = points.slice(start, end + 1);
    const midT = (start + end) / 2 / (colorSteps - 1);
    const path = mapView.Paths.add(chunk, {
      color: lerpColorSequence(config.routeGradientColors, midT),
      width: 0.6,
      displayArrowsOnPath: Boolean(range),
      animateArrowsOnPath: Boolean(range),
      showPulse: false,
      animateDrawing: false,
    });
    gradientPaths.push(path);
    i = end;
  }
}

function clearGradientPaths(mapView: MapView): void {
  if (gradientPaths.length) {
    for (const path of gradientPaths) mapView.Paths.remove(path);
    gradientPaths = [];
  }
}

/** How much closer the camera zooms in on the destination once the route intro reaches it. */
const DEST_ARRIVAL_ZOOM_IN = 0.5;
/** Tilt the camera goes back to on arrival (the walk itself is flat, top-down 2D). */
const DEST_ARRIVAL_PITCH = config.defaultCameraPitch ?? 55;

/** Told when the route intro flips the camera between 2D and 3D, so the tilt toggle can follow. */
let introPitchListener: ((mode: "2d" | "3d") => void) | null = null;
export function onRouteIntroPitchChange(listener: (mode: "2d" | "3d") => void): void {
  introPitchListener = listener;
}

/** Bumped on every new route so a superseded intro animation stops instead of fighting the next camera move. */
let routeIntroRun = 0;

/** Metres between two lat/lng points (equirectangular — plenty accurate at venue scale). */
export function metersBetween(a: Coordinate, b: Coordinate): number {
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLng = ((b.longitude - a.longitude) * Math.PI) / 180;
  const x = dLng * Math.cos((((a.latitude + b.latitude) / 2) * Math.PI) / 180);
  return Math.hypot(x, dLat) * 6371000;
}

/** Point `distance` metres along the polyline, given each vertex's cumulative distance. */
function pointAlongRoute(path: Coordinate[], cumulative: number[], distance: number): Coordinate {
  let i = 1;
  while (i < cumulative.length - 1 && cumulative[i] < distance) i++;
  const span = cumulative[i] - cumulative[i - 1] || 1;
  const t = Math.min(1, Math.max(0, (distance - cumulative[i - 1]) / span));
  const a = path[i - 1];
  const b = path[i];
  return new Coordinate({
    latitude: a.latitude + (b.latitude - a.latitude) * t,
    longitude: a.longitude + (b.longitude - a.longitude) * t,
    floorId: a.floorId,
  });
}

/**
 * Camera intro for a freshly drawn route: first glide to the From stand so
 * the visitor sees where they start, hold a beat, then travel the whole path
 * to the destination with the moving point kept at the centre of the screen
 * — so the visitor feels the route as they'll walk it and the camera comes
 * to rest where they're headed. Fire-and-forget — the route result is rendered right away rather
 * than waiting on the animation. Orientation follows VITE_DEFAULT_CAMERA_BEARING
 * (fixed, never turning with the path, which would be dizzy). Any touch on
 * the map, or a newer route, stops it where it is.
 */
function playRouteIntro(
  mapView: MapView,
  from: MapTarget,
  to: MapTarget,
  routePath: Coordinate[],
  /** Keep the camera flat (2D) on arrival too, instead of tilting back to 3D — used for the Nearest Exit route. */
  stay2D = false,
): void {
  const run = ++routeIntroRun;
  // The route's own coordinates already run From → To, the walk's direction.
  const path = routePath;
  const bearing = config.defaultCameraBearing !== null ? { bearing: config.defaultCameraBearing } : {};
  // Same zoom as the default view (VITE_DEFAULT_CAMERA_ZOOM) for the whole intro — min = max pins
  // it exactly. Unset: fit each target with a minimum of 18, as before.
  const introZoom =
    config.defaultCameraZoom !== null
      ? { minZoomLevel: config.defaultCameraZoom, maxZoomLevel: config.defaultCameraZoom }
      : { minZoomLevel: 18 };
  const cancelled = () => run !== routeIntroRun;
  const stopOnTouch = () => {
    if (run === routeIntroRun) routeIntroRun++;
  };
  window.addEventListener("pointerdown", stopOnTouch, { once: true, capture: true });

  // There is only ever one destination pin: the intro borrows it, walks it
  // along the route at the centre of the screen, and parks it back on the
  // stand — so the visitor sees *what* is moving and where it stops.
  // `pinIntroRun` lets only the latest intro touch it (a superseded intro
  // must not snap it back while the newer one is walking it).
  const pin = destinationMarker;
  const pinEl = pin?.contentEl.querySelector<HTMLElement>(".dest-map-pin") ?? null;
  pinIntroRun = run;
  let arrived = false;
  const ownsPin = () => pin !== null && pin === destinationMarker && pinIntroRun === run;
  const arrive = () => {
    if (arrived || !ownsPin()) return;
    arrived = true;
    pinEl?.classList.remove("is-travelling");
    pinEl?.classList.add("is-arrived");
    startDestinationGlow(mapView, to);
  };

  /** Centres on the destination, tilts back to 3D and zooms in a further 0.5 level — the stand rises after this. */
  const zoomInOnDestination = async () => {
    const center = to instanceof Coordinate ? to : "center" in to ? to.center : to.coordinate;
    introPitchListener?.(stay2D ? "2d" : "3d");
    try {
      await mapView.Camera.animateTo(
        { center, zoomLevel: mapView.Camera.zoomLevel + DEST_ARRIVAL_ZOOM_IN, pitch: stay2D ? 0 : DEST_ARRIVAL_PITCH },
        { duration: 1300, easing: "ease-in-out" },
      );
    } catch {
      // Interrupted by a touch or a newer route — fine to stop where it is.
    }
  };

  stopDestinationGlow(mapView);
  pinEl?.classList.remove("is-arrived");
  // "You Are Here" stays out of the way for the whole route play; back in `finally`.
  setKioskMarkerHidden(true);
  if (pin && path.length >= 2) {
    mapView.Markers.setPosition(pin, path[0]);
    pinEl?.classList.add("is-travelling");
  }

  // The whole route plays flat (top-down 2D) so the path reads clearly; the
  // camera tilts back to 3D on arrival, just before the stand rises.
  introPitchListener?.("2d");

  void (async () => {
    try {
      await mapView.Camera.focusOn([from], { ...introZoom, duration: 2000, easing: "ease-in-out", pitch: 0, ...bearing });
      if (cancelled()) return;
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
      if (cancelled()) return;

      if (path.length < 2) {
        await mapView.Camera.focusOn([to], { ...introZoom, duration: 3000, easing: "ease-in-out", pitch: 0, ...bearing });
        if (!cancelled()) await zoomInOnDestination();
        return;
      }

      // Hold the zoom the From view settled on for the whole walk.
      const zoomLevel = config.defaultCameraZoom ?? Math.max(18, mapView.Camera.zoomLevel);
      const pitch = 0;
      const walkBearing = config.defaultCameraBearing ?? mapView.Camera.bearing;
      // Settle on the walk's first point (the From end) before moving.
      await mapView.Camera.animateTo(
        { center: path[0], zoomLevel, pitch, bearing: walkBearing },
        { duration: 1200, easing: "ease-in-out" },
      );
      if (cancelled()) return;

      const cumulative = [0];
      for (let i = 1; i < path.length; i++) cumulative.push(cumulative[i - 1] + metersBetween(path[i - 1], path[i]));
      const total = cumulative[cumulative.length - 1];
      // ~22 m/s is a quick flythrough: a 100 m walk takes ~4.5 s, capped so long routes don't drag.
      const durationMs = Math.min(14000, Math.max(3500, (total / 22) * 1000));

      await new Promise<void>((resolve) => {
        const startedAt = performance.now();
        const frame = (now: number) => {
          if (cancelled()) return resolve();
          const t = Math.min(1, (now - startedAt) / durationMs);
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const center = pointAlongRoute(path, cumulative, eased * total);
          mapView.Camera.set({ center, zoomLevel, pitch, bearing: walkBearing });
          if (pin && ownsPin()) mapView.Markers.setPosition(pin, center);
          if (t < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
      if (cancelled()) return;

      // The route ends at the stand's edge/door; step the pin from there onto
      // the stand's own anchor — the exact spot its fixed pin belongs.
      // Meanwhile the camera closes in a little on the stand for the arrival effect.
      const zoom = zoomInOnDestination();
      if (pin && ownsPin()) await mapView.Markers.animateTo(pin, to, { duration: 700, easing: "ease-in-out" });
      await zoom;
    } catch {
      // A newer camera move interrupted this one — nothing to recover.
    } finally {
      window.removeEventListener("pointerdown", stopOnTouch, { capture: true });
      // Only the latest intro restores it — a superseded one must not show it mid-play of the next.
      if (pinIntroRun === run) setKioskMarkerHidden(false);
      // Finished or stopped by a touch: the pin always ends on the stand.
      if (pin && ownsPin()) {
        mapView.Markers.setPosition(pin, to);
        arrive();
      }
    }
  })();
}

/**
 * Runs Mappedin's pathfinder between two stands and draws route.
 */
export async function routeBetween(
  mapView: MapView,
  from: MapTarget,
  to: MapTarget,
  options: { accessible?: boolean; intro?: boolean; stay2D?: boolean } = {},
): Promise<RouteResult> {
  clearGradientPaths(mapView);
  mapView.Navigation.clear();
  liveRoute = null;

  const directions = await mapView.getDirections(from, to, {
    accessible: options.accessible ?? false,
    smoothing: true,
  });

  if (!directions) return { status: "no-route" };
  if (directions.coordinates.length === 0) return { status: "same-stand" };

  // The route line is drawn entirely by `drawGradientPath` below (which is
  // also where the directional arrows now live) — `Navigation.draw()` is
  // deliberately not used here. Departure/arrival are already marked by
  // `addKioskMarker`/`setDestinationMarker`, so its own markers would be
  // redundant, and its path can't be hidden cleanly: `color: "rgba(0,0,0,0)"`
  // silently renders as opaque black (Three.js's Color type has no alpha
  // channel), which used to draw a second, unsmoothed line underneath the
  // gradient one — the actual cause of the squiggly/tangled look.
  drawGradientPath(mapView, directions.coordinates, directions.distance);
  setLiveRoute(directions.coordinates);

  // A silent refresh of a route already on screen (see the live-location
  // refresh in main.ts) must not replay the camera flythrough.
  if (options.intro !== false) playRouteIntro(mapView, from, to, directions.coordinates, options.stay2D);

  return {
    status: "found",
    directions,
    distanceMeters: directions.distance,
    steps: Math.round(directions.distance / config.stepLengthMeters),
    etaMinutes: Math.max(1, Math.round(directions.distance / config.walkingSpeedMps / 60)),
  };
}

export function clearRoute(mapView: MapView): void {
  liveRoute = null;
  clearGradientPaths(mapView);
  mapView.Navigation.clear();
  clearDestinationMarker(mapView);
}

// --- POI Amenity Markers ---------------------------------------------------

export function renderPOIMarkers(
  mapView: MapView,
  pois: { poi: PointOfInterest; category: string; color: string; svg: string }[],
): void {
  clearPOIMarkers(mapView);

  for (const entry of pois) {
    try {
      const marker = mapView.Markers.add(
        entry.poi,
        `<div class="poi-marker" style="--poi-color: ${entry.color}" aria-label="${entry.category}">
          <div class="poi-icon">${entry.svg}</div>
        </div>`,
        { rank: "medium", interactive: true },
      );
      if (marker) poiMarkers.push(marker);
    } catch {
      // POI may not be anchorable on this floor
    }
  }
}

export function clearPOIMarkers(mapView: MapView): void {
  for (const m of poiMarkers) {
    try { mapView.Markers.remove(m); } catch { }
  }
  poiMarkers.length = 0;
}

export function focusOnPOI(mapView: MapView, poi: PointOfInterest): void {
  void mapView.Camera.focusOn([poi], { minZoomLevel: 18, duration: 600 });
}

// --- Along-the-Route Amenity Discovery ------------------------------------

export interface AlongRoutePOI {
  poi: PointOfInterest;
  distanceFromRouteMeters: number;
  detourTimeSeconds: number;
  category: string;
  color: string;
  svg: string;
}

/**
 * Finds POIs within `thresholdMeters` of the route polyline.
 * Uses Mappedin's `mapData.getDistance()` for accurate 3D coordinate distance.
 * Returns results sorted by proximity to route, capped at `maxResults`.
 */
export function findPOIsAlongRoute(
  mapData: MapData,
  directions: Directions,
  allPOIs: { poi: PointOfInterest; category: string; color: string; svg: string }[],
  options: { thresholdMeters?: number; maxResults?: number } = {},
): AlongRoutePOI[] {
  const threshold = options.thresholdMeters ?? 15;
  const maxResults = options.maxResults ?? 5;

  const routeCoords = directions.coordinates;
  if (!routeCoords || routeCoords.length < 2) return [];

  const results: AlongRoutePOI[] = [];

  for (const entry of allPOIs) {
    let minDistance = Infinity;

    for (const routePt of routeCoords) {
      const dist = mapData.getDistance(routePt, entry.poi);
      if (dist < minDistance) minDistance = dist;
      if (minDistance <= threshold) break;
    }

    if (minDistance <= threshold) {
      const detourTimeSeconds = Math.round(
        (minDistance * 2) / config.walkingSpeedMps,
      );
      results.push({
        poi: entry.poi,
        distanceFromRouteMeters: Math.round(minDistance * 10) / 10,
        detourTimeSeconds,
        category: entry.category,
        color: entry.color,
        svg: entry.svg,
      });
    }
  }

  return results
    .sort((a, b) => a.distanceFromRouteMeters - b.distanceFromRouteMeters)
    .slice(0, maxResults);
}

// --- Multi-Floor Level Switcher ---------------------------------------------

/**
 * Returns all floors of the venue's active floor stack, ordered low-to-high
 * by elevation (matches how a physical elevator panel lists levels).
 */
export function getFloors(mapData: MapData): Floor[] {
  return [...mapData.getByType("floor")].sort((a, b) => a.elevation - b.elevation);
}

export function getCurrentFloor(mapView: MapView): Floor {
  return mapView.currentFloor;
}

/**
 * Switches the active floor. Resolves once the SDK has committed the change
 * (`floor-change` event), so callers can safely update UI state afterwards.
 */
export function switchFloor(mapView: MapView, floorId: string): Promise<void> {
  if (mapView.currentFloor?.id === floorId) return Promise.resolve();
  return new Promise((resolve) => {
    const handler = () => {
      mapView.off("floor-change", handler);
      resolve();
    };
    mapView.on("floor-change", handler);
    mapView.setFloor(floorId);
  });
}

/**
 * Subscribes to floor changes (manual picker taps, or a multi-level route
 * auto-advancing floors) and returns an unsubscribe function.
 */
export function onFloorChange(mapView: MapView, callback: (floor: Floor) => void): () => void {
  const handler = (event: { floor: Floor }) => callback(event.floor);
  mapView.on("floor-change", handler);
  return () => mapView.off("floor-change", handler);
}

// --- Camera View Presets ----------------------------------------------------

/**
 * Named vantage points, captured from live venue geometry (never hardcoded
 * coordinates) via `Camera.getFocusOnTransform`. Populated once on boot by
 * `captureVenuePresets` and reused for cinematic idle resets instead of
 * re-computing space bounds every time.
 */
const cameraPresets = new Map<string, TCameraTarget>();

/**
 * Computes and caches the "venue-overview" (angled 3D, whole floor) and,
 * when a kiosk target is known — i.e. the "You Are Here" marker's Space or
 * Point of Interest, either the configured VITE_KIOSK_LOCATION_EXTERNAL_ID
 * match or the directory[0] fallback — the "kiosk-overview" (angled 3D,
 * focused on that same spot) camera presets. VITE_DEFAULT_CAMERA_* pins
 * bearing/pitch/zoom on both.
 *
 * Also caches "default-view" — the kiosk-overview when `useKioskAsDefaultView`
 * is true, so the map's default framing centers on wherever "You Are Here"
 * actually is instead of the venue's geometric middle — falling back to
 * venue-overview otherwise. That also doubles as "locked-view", so a boot
 * with VITE_DEFAULT_CAMERA_LOCK=true resets back to the exact same pinned
 * framing every time, on every device.
 */
export function captureVenuePresets(
  mapView: MapView,
  mapData: MapData,
  kioskTarget?: MapTarget | null,
  useKioskAsDefaultView = false,
): void {
  const spaces = mapData.getByType("space");
  let venuePreset: TCameraTarget | null = null;
  if (spaces.length > 0) {
    venuePreset = mapView.Camera.getFocusOnTransform(spaces, {
      pitch: config.defaultCameraPitch ?? 55,
      bearing: config.defaultCameraBearing ?? 0,
    });
    if (config.defaultCameraZoom !== null) venuePreset.zoomLevel = config.defaultCameraZoom;
    cameraPresets.set("venue-overview", venuePreset);
  }

  let kioskPreset: TCameraTarget | null = null;
  if (kioskTarget) {
    kioskPreset = mapView.Camera.getFocusOnTransform(kioskTarget, {
      pitch: config.defaultCameraPitch ?? 55,
      minZoomLevel: 18,
    });
    if (config.defaultCameraBearing !== null) kioskPreset.bearing = config.defaultCameraBearing;
    if (config.defaultCameraZoom !== null) kioskPreset.zoomLevel = config.defaultCameraZoom;
    cameraPresets.set("kiosk-overview", kioskPreset);
  }

  const defaultPreset = (useKioskAsDefaultView ? kioskPreset : null) ?? venuePreset;
  if (defaultPreset) {
    cameraPresets.set("default-view", defaultPreset);
    cameraPresets.set("locked-view", defaultPreset);
  }
}

/** Smoothly glides the camera to a preset captured by `captureVenuePresets`. Falls back to whole-venue focus if the preset isn't cached yet. */
export function applyCameraPreset(mapView: MapView, mapData: MapData, name: string, durationMs = 1200): void {
  const preset = cameraPresets.get(name);
  if (preset) {
    void mapView.Camera.animateTo(preset, { duration: durationMs, easing: "ease-in-out" });
  } else {
    focusWholeVenue(mapView, mapData);
  }
}

/**
 * Snapshots the camera's exact current pose (center, zoom, bearing, pitch)
 * as a named preset. Used to remember the operator's locked framing — pan
 * and zoom stay usable while locked, so a visitor can drift away from it,
 * and this is what `applyCameraPreset` restores on reset.
 */
export function captureCurrentCameraAsPreset(mapView: MapView, name: string): void {
  const { Camera } = mapView;
  cameraPresets.set(name, {
    center: Camera.center,
    zoomLevel: Camera.zoomLevel,
    bearing: Camera.bearing,
    pitch: Camera.pitch,
  });
}

// --- Zone / Pavilion Spatial Highlight ---------------------------------------

/**
 * Deterministically derives a vivid, stable accent color from a category
 * name so zone tinting works for any venue's category set without a
 * hardcoded color table.
 */
export function zoneColorForCategory(categoryName: string): string {
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = (hash * 31 + categoryName.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `hsl(${hue}, 72%, 52%)`;
}

/**
 * Tints spaces belonging to the selected zone/category and dims the rest to
 * 25% opacity. Passing `null` for `matchingSpaces` restores every space to
 * its default appearance.
 */
export function highlightZone(
  mapView: MapView,
  allSpaces: Space[],
  matchingSpaces: Space[] | null,
  zoneColor?: string,
): void {
  if (!matchingSpaces) {
    for (const space of allSpaces) {
      mapView.updateState(space, { color: "initial", opacity: 1 });
    }
    return;
  }

  const matchingIds = new Set(matchingSpaces.map((s) => s.id));
  for (const space of allSpaces) {
    if (matchingIds.has(space.id)) {
      mapView.updateState(space, { color: zoneColor ?? "initial", opacity: 1 });
    } else {
      mapView.updateState(space, { color: "initial", opacity: 0.25 });
    }
  }
}

// --- Camera Position Lock ----------------------------------------------------

/**
 * Locks/unlocks bearing and pitch (rotation + tilt) interactions while
 * leaving pan and zoom fully functional, so a kiosk operator can pin the
 * map to a fixed orientation for public use without disabling navigation.
 */
export function setCameraRotationLock(mapView: MapView, locked: boolean): void {
  mapView.Camera.interactions.set({ bearingAndPitch: !locked, pan: true, zoom: true });
}
